import { AxiosError, AxiosResponse } from "axios";
import { IGetListRequest, IResponse, IServerError } from "../../models";

import { IDropDownListResponse } from "../../models/common/IDropDownListResponse";
import { format422Errors } from "../../common/Common";
import http from "../../common/http-commons";

export const TransactionsServices = {
  // #################################################### Get Direct Deposit List on the base of screen processing type  ######################################################
  getDirectDepositList: async (
    runGetListAPICall: boolean,
    screenType: number,
    accountant: string,
    client: string,
    clientType: string | number,
    type: string,
    transactionMethod: string | number | null,
    dateAppliesOn: string | number,
    startDate: string | null,
    endDate: string | null,
    data: IGetListRequest,
    navigateToLogout: (path: string) => void,
  ): Promise<IServerError | IResponse | null | any> => {
    if (!runGetListAPICall) return null;
    if (!accountant) return null;

    try {
      const response: AxiosResponse<any> = await http.get<any>(
        "/v1/direct-deposit/?screen_type=" +
          screenType +
          "&accountant_id=" +
          accountant +
          "&client_id=" +
          client +
          "&client_type=" +
          clientType +
          "&type=" +
          type +
          "&date_applies_on=" +
          dateAppliesOn +
          (startDate ? "&start_date=" + startDate : "") +
          (endDate ? "&end_date=" + endDate : "") +
          "&search_query=" +
          data.search_query +
          "&order_by=" +
          data.order_by +
          "&order_direction=" +
          data.order_direction +
          "&page=" +
          data.page +
          "&size=" +
          data.size,
      );

      if (response.status === 200) {
        return response.data;
      } else if (response.status === 400) {
        return {
          status: false,
          message: "Bad Request.",
          error: true,
        };
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }
      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      if (axiosError && axiosError.status === 401) {
        navigateToLogout("/logout");
        return {
          status: false,
          message: "UnAuthrized",
        };
      } else {
        return {
          status: false,
          message: errorMessage,
        };
      }
    }
  },

  // #################################################### Get Direct Deposit List on the base of screen processing type  ######################################################
  getDirectDepositForPaymentList: async (
    runGetListAPICall: boolean,
    screenType: number,
    accountant: string,
    client: string,
    clientType: string | number,
    type: string,
    transactionMethod: string | number,
    dateAppliesOn: string | number,
    startDate: string | null,
    endDate: string | null,
    data: IGetListRequest,
    navigateToLogout: (path: string) => void,
  ): Promise<IServerError | IResponse | null | any> => {
    if (!runGetListAPICall) return null;

    try {
      const response: AxiosResponse<any> = await http.get<any>(
        "/v1/direct-deposit/for-payment/?screen_type=" +
          screenType +
          "&accountant_id=" +
          accountant +
          "&client_id=" +
          client +
          "&client_type=" +
          clientType +
          "&type=" +
          type +
          "&date_applies_on=" +
          dateAppliesOn +
          (startDate ? "&start_date=" + startDate : "") +
          (endDate ? "&end_date=" + endDate : "") +
          "&search_query=" +
          data.search_query +
          "&order_by=" +
          data.order_by +
          "&order_direction=" +
          data.order_direction +
          "&page=" +
          data.page +
          "&size=" +
          data.size,
      );

      if (response.status === 200) {
        return response.data;
      } else if (response.status === 400) {
        return {
          status: false,
          message: "Bad Request.",
          error: true,
        };
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }
      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      if (axiosError && axiosError.status === 401) {
        navigateToLogout("/logout");
        return {
          status: false,
          message: "UnAuthrized",
        };
      } else {
        return {
          status: false,
          message: errorMessage,
        };
      }
    }
  },

  rollBackAfterCollectionTransactions: async (
    requestPayload: any,
  ): Promise<any | IServerError | IResponse | any> => {
    try {
      const response: AxiosResponse<any> = await http.post<any>(
        `/v1/direct-deposit-roll-back/`,
        requestPayload,
      );

      if (response.status === 201) {
        return response;
      } else if (response.status === 400) {
        return response.data;
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      return {
        status: false,
        message: errorMessage,
      };
    }
  },

  // #################################################### Get Direct Deposit Remittances List on the base of screen processing type  ######################################################
  getDirectDepositRemittancesList: async (
    runGetListAPICall: boolean,
    screenType: number,
    accountant: string,
    client: string,
    clientType: string,
    frequency: string | null,
    remittanceYear: string | null,
    remittanceMonth: string | null,
    data: IGetListRequest,
    navigateToLogout: (path: string) => void,
  ): Promise<IServerError | IResponse | null | any> => {
    if (!runGetListAPICall) return null;
    if (!accountant) return null;

    try {
      const response: AxiosResponse<any> = await http.get<any>(
        "/v1/direct-deposit/cra/rq/eht/?screen_type=" +
          screenType +
          "&accountant_id=" +
          accountant +
          "&client_id=" +
          client +
          "&client_type=" +
          clientType +
          "&frequency=" +
          frequency +
          "&remittance_year=" +
          remittanceYear +
          "&remittance_date=" +
          remittanceMonth +
          "&search_query=" +
          data.search_query +
          "&order_by=" +
          data.order_by +
          "&order_direction=" +
          data.order_direction +
          "&page=" +
          data.page +
          "&size=" +
          data.size,
      );

      if (response.status === 200) {
        return response.data;
      } else if (response.status === 400) {
        return {
          status: false,
          message: "Bad Request.",
          error: true,
        };
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }
      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      if (axiosError && axiosError.status === 401) {
        navigateToLogout("/logout");
        return {
          status: false,
          message: "UnAuthrized",
        };
      } else {
        return {
          status: false,
          message: errorMessage,
        };
      }
    }
  },

  // #################################################### Update a Direct Deposit Collections ######################################################
  updateDirectDepositForCollections: async (
    requestPayload: any,
  ): Promise<IServerError | IResponse | any> => {
    try {
      console.log("requestPayload", requestPayload);
      const response: AxiosResponse<any> = await http.put<any>(
        `/v1/direct-deposit/collections-data/`,
        { paymentList: requestPayload },
      );

      if (response.status === 201) {
        return response;
      } else if (response.status === 400) {
        return response.data;
      } else if (response.status === 401) {
        // window.location.href = '/logout';
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      if (axiosError && axiosError.status === 401) {
        requestPayload.navigateToLogout("/logout");
        return {
          status: false,
          message: "UnAuthrized",
        };
      } else if (axiosError && axiosError.status === 422) {
        return {
          status: false,
          message: `Errors : \n${format422Errors(axiosError?.response?.data)}`,
        };
      } else {
        return {
          status: false,
          message: errorMessage,
        };
      }
    }
  },

  // #################################################### Update a Direct Deposit Payment Collections ######################################################
  updateDirectDepositForPaymentCollections: async (
    requestPayload: any,
  ): Promise<IServerError | IResponse | any> => {
    try {
      const response: AxiosResponse<any> = await http.put<any>(
        `/v1/direct-deposit/`,
        { paymentList: requestPayload },
      );

      if (response.status === 201) {
        return response;
      } else if (response.status === 400) {
        return response.data;
      } else if (response.status === 401) {
        // window.location.href = '/logout';
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      if (axiosError && axiosError.status === 401) {
        requestPayload.navigateToLogout("/logout");
        return {
          status: false,
          message: "UnAuthrized",
        };
      } else if (axiosError && axiosError.status === 422) {
        return {
          status: false,
          message: `Errors : \n${format422Errors(axiosError?.response?.data)}`,
        };
      } else {
        return {
          status: false,
          message: errorMessage,
        };
      }
    }
  },

  // #################################################### Ready ######################################################
  getReadyTransacitonsList: async (
    runGetListAPICall: boolean,
    screenType: string,
    accountant: string,
    client: string,
    clientType: string,
    type: string,
    dateAppliesOn: string,
    startDate: string | null,
    endDate: string | null,
    data: IGetListRequest,
    navigateToLogout: (path: string) => void,
  ): Promise<IServerError | IResponse | null | any> => {
    // if (!runGetListAPICall) return null;

    // try {
    //   const response: AxiosResponse<any> = await http.get<any>(
    //     "/v1/direct-deposit/?screen_type=" +
    //       screenType +
    //       "&accountant_id=" +
    //       accountant +
    //       "&client_id=" +
    //       client +
    //       "&client_type=" +
    //       clientType +
    //       "&type=" +
    //       type +
    //       "&date_applies_on=" +
    //       dateAppliesOn +
    //       (startDate ? "&start_date=" + startDate : "") +
    //       (endDate ? "&end_date=" + endDate : "") +
    //       "&search_query=" +
    //       data.search_query +
    //       "&order_by=" +
    //       data.order_by +
    //       "&order_direction=" +
    //       data.order_direction +
    //       "&page=" +
    //       data.page +
    //       "&size=" +
    //       data.size,
    //   );

    //   if (response.status === 200) {
    //     return response.data;
    //   } else if (response.status === 400) {
    //     return {
    //       status: false,
    //       message: "Bad Request.",
    //       error: true,
    //     };
    //   } else if (response.status === 500) {
    //     return {
    //       status: false,
    //       message: "Something went wrong.",
    //       error: true,
    //     };
    //   }
    //   return {
    //     status: false,
    //     message: "Something went wrong please try again later.",
    //   };
    // } catch (error) {
    //   const axiosError = error as AxiosError;
    //   console.error("An error occurred:", axiosError);
    //   const errorMessage =
    //     axiosError.response && typeof axiosError.response.data === "string"
    //       ? axiosError.response.data
    //       : "Unknown error";
    //   if (axiosError && axiosError.status === 401) {
    //     navigateToLogout("/logout");
    //     return {
    //       status: false,
    //       message: "UnAuthrized",
    //     };
    //   } else {
    //     return {
    //       status: false,
    //       message: errorMessage,
    //     };
    //   }
    // }
    const RQFixedResponse: any = {
      page: 1,
      size: 25,
      total: 2,
      items: [
        {
          transactionId: 2,
          clientId: "698996ab01cce37fb81463fc",
          accountantId: "68f0858725210487021845da",
          type: "Payroll",
          amount: 59.0,
          createdOn: "Feb 23 2026 08:42:38 AM",
          payPeriod: "MONTHLY",
          payPeriodEndDate: "Jan 31, 2025",
          lastTab: 900,
          fromLastTabOn: "Ready",
          transactionErrorId: null,
          paymentMethod: 200,
          status: 100,
          payrollSheduleId: "6940fd99d366fdb66630cfa1",
          payPeriodPaymentDate: "2026-02-25",
          errorMessage: null,
          uploadDate: null,
          uploadTime: null,
          fromLastTabOnDate: "2026-03-04T12:34:05.551000",
          id: "699dc84fc2ea47f2f04d95cf",
          clientName: "ALL Provinces Employes Testing",
          orders: [
            {
              transactionId: 2,
              clientId: "698996ab01cce37fb81463fc",
              accountantId: "68f0858725210487021845da",
              type: "Payroll",
              amount: 34.54,
              createdOn: "Feb 23 2026 08:42:38 AM",
              payPeriod: "MONTHLY",
              payPeriodEndDate: "Jan 31, 2025",
              lastTab: 900,
              fromLastTabOn: "Ready",
              transactionErrorId: null,
              paymentMethod: 200,
              status: 100,
              payrollSheduleId: "6940fd99d366fdb66630cfa1",
              payPeriodPaymentDate: "2026-02-25",
              errorMessage: null,
              uploadDate: null,
              uploadTime: null,
              fromLastTabOnDate: "2026-03-04T12:34:05.551000",
              id: "699dc84fc2ea47f2f04d95cf",
              clientName: "ALL Provinces Employes Testing",
              parentId: "699dc84fc2ea47f2f04d95cf",
            },
            {
              transactionId: 2,
              clientId: "698996ab01cce37fb81463fc",
              accountantId: "68f0858725210487021845da",
              type: "cra",
              amount: 12.23,
              createdOn: "Feb 23 2026 08:44:33 AM",
              payPeriod: "MONTHLY",
              payPeriodEndDate: "Jan 31, 2025",
              lastTab: 900,
              fromLastTabOn: "Ready",
              transactionErrorId: null,
              paymentMethod: 200,
              status: 100,
              id: "69aa71f5ca10d5c6bec256cd",
              payrollSheduleId: "6940fd99d366fdb66630cfa1",
              payPeriodPaymentDate: "2026-02-25",
              errorMessage: null,
              uploadDate: null,
              uploadTime: null,
              fromLastTabOnDate: "2026-03-04T12:34:41.511000",
              craRqEhtLastTabOn: 0,
              clientName: "ALL Provinces Employes Testing",
              parentId: "699dc84fc2ea47f2f04d95cf",
            },
            {
              transactionId: 2,
              clientId: "698996ab01cce37fb81463fc",
              accountantId: "68f0858725210487021845da",
              type: "rq",
              amount: 12.23,
              createdOn: "Feb 23 2026 08:44:33 AM",
              payPeriod: "MONTHLY",
              payPeriodEndDate: "Jan 31, 2025",
              lastTab: 900,
              fromLastTabOn: "Ready",
              transactionErrorId: null,
              paymentMethod: 200,
              status: 100,
              id: "69aa7215ca10d5c6bec256ce",
              payrollSheduleId: "6940fd99d366fdb66630cfa1",
              payPeriodPaymentDate: "2026-02-25",
              errorMessage: null,
              uploadDate: null,
              uploadTime: null,
              fromLastTabOnDate: "2026-03-04T12:35:57.070000",
              craRqEhtLastTabOn: 0,
              clientName: "ALL Provinces Employes Testing",
              parentId: "699dc84fc2ea47f2f04d95cf",
            },
          ],
        },
        {
          transactionId: 2,
          clientId: "6940fccbd366fdb66630cf9c",
          accountantId: "68f0858725210487021845da",
          type: "Payroll",
          amount: 71.23,
          createdOn: "Feb 23 2026 08:42:38 AM",
          payPeriod: "MONTHLY",
          payPeriodEndDate: "Jan 31, 2025",
          lastTab: 700,
          fromLastTabOn: "Ready",
          transactionErrorId: null,
          paymentMethod: 200,
          status: 100,
          payrollSheduleId: "6940fd99d366fdb66630cfa1",
          payPeriodPaymentDate: "2026-02-25",
          errorMessage: null,
          uploadDate: null,
          uploadTime: null,
          fromLastTabOnDate: "2026-03-10T10:12:49.369000",
          id: "699dc84fc2ea47f2f04d94cf",
          craRqEhtLastTabOn: null,
          clientName: "Reports and Screen Testing",
          orders: [
            {
              transactionId: 2,
              clientId: "6940fccbd366fdb66630cf9c",
              accountantId: "68f0858725210487021845da",
              type: "Payroll",
              amount: 34.54,
              createdOn: "Feb 23 2026 08:42:38 AM",
              payPeriod: "MONTHLY",
              payPeriodEndDate: "Jan 31, 2025",
              lastTab: 700,
              fromLastTabOn: "Ready",
              transactionErrorId: null,
              paymentMethod: 200,
              status: 100,
              payrollSheduleId: "6940fd99d366fdb66630cfa1",
              payPeriodPaymentDate: "2026-02-25",
              errorMessage: null,
              uploadDate: null,
              uploadTime: null,
              fromLastTabOnDate: "2026-03-10T10:12:49.369000",
              id: "699dc84fc2ea47f2f04d94cf",
              craRqEhtLastTabOn: null,
              clientName: "Reports and Screen Testing",
              parentId: "699dc84fc2ea47f2f04d94cf",
            },
            {
              transactionId: 2,
              clientId: "6940fccbd366fdb66630cf9c",
              accountantId: "68f0858725210487021845da",
              type: "cra",
              amount: 12.23,
              createdOn: "Feb 23 2026 08:44:33 AM",
              payPeriod: "MONTHLY",
              payPeriodEndDate: "Jan 31, 2025",
              lastTab: 700,
              fromLastTabOn: "Ready",
              transactionErrorId: null,
              paymentMethod: 200,
              status: 100,
              id: "69a7c20a1f507c1cceaa49f0",
              payrollSheduleId: "6940fd99d366fdb66630cfa1",
              payPeriodPaymentDate: "2026-02-25",
              errorMessage: null,
              uploadDate: null,
              uploadTime: null,
              fromLastTabOnDate: "2026-03-10T10:12:49.582000",
              craRqEhtLastTabOn: 0,
              clientName: "Reports and Screen Testing",
              parentId: "699dc84fc2ea47f2f04d94cf",
            },
            {
              transactionId: 2,
              clientId: "6940fccbd366fdb66630cf9c",
              accountantId: "68f0858725210487021845da",
              type: "rq",
              amount: 12.23,
              createdOn: "Feb 23 2026 08:44:33 AM",
              payPeriod: "MONTHLY",
              payPeriodEndDate: "Jan 31, 2025",
              lastTab: 700,
              fromLastTabOn: "Ready",
              transactionErrorId: null,
              paymentMethod: 200,
              status: 100,
              id: "69a7c2441f507c1cceaa49f1",
              payrollSheduleId: "6940fd99d366fdb66630cfa1",
              payPeriodPaymentDate: "2026-02-25",
              errorMessage: null,
              uploadDate: null,
              uploadTime: null,
              fromLastTabOnDate: "2026-03-10T10:12:49.798000",
              craRqEhtLastTabOn: 0,
              clientName: "Reports and Screen Testing",
              parentId: "699dc84fc2ea47f2f04d94cf",
            },
            {
              transactionId: 2,
              clientId: "6940fccbd366fdb66630cf9c",
              accountantId: "68f0858725210487021845da",
              type: "eht",
              amount: 12.23,
              createdOn: "Feb 23 2026 08:44:33 AM",
              payPeriod: "MONTHLY",
              payPeriodEndDate: "Jan 31, 2025",
              lastTab: 700,
              fromLastTabOn: "Ready",
              transactionErrorId: null,
              paymentMethod: 200,
              status: 100,
              id: "69a7c2771f507c1cceaa49f2",
              payrollSheduleId: "6940fd99d366fdb66630cfa1",
              payPeriodPaymentDate: "2026-02-25",
              errorMessage: null,
              uploadDate: null,
              uploadTime: null,
              fromLastTabOnDate: "2026-03-10T10:12:50.011000",
              craRqEhtLastTabOn: 0,
              clientName: "Reports and Screen Testing",
              parentId: "699dc84fc2ea47f2f04d94cf",
            },
          ],
        },
      ],
      keyField: {
        field: "id",
        templateUrl: "/designation/setup/id",
      },
      headerColumns: [
        {
          field: "transactionId",
          headerName: "ID",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "center",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 0,
          frozenColumn: false,
          footer: null,
          minWidth: null,
          maxWidth: null,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "clientName",
          headerName: "Party",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 1,
          frozenColumn: false,
          footer: null,
          minWidth: 250,
          maxWidth: 250,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "type",
          headerName: "Type",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 2,
          frozenColumn: false,
          footer: null,
          minWidth: null,
          maxWidth: null,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "amount",
          headerName: "Amount",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "right",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 2,
          frozenColumn: false,
          footer: null,
          minWidth: null,
          maxWidth: null,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "createdOn",
          headerName: "Created On",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 2,
          frozenColumn: false,
          footer: null,
          minWidth: null,
          maxWidth: null,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "payPeriod",
          headerName: "Pay Period",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 2,
          frozenColumn: false,
          footer: null,
          minWidth: null,
          maxWidth: null,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "payPeriodEndDate",
          headerName: "Pay Period End Date",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 2,
          frozenColumn: false,
          footer: null,
          minWidth: null,
          maxWidth: null,
          fieldName: null,
          edr: "edr",
        },
      ],
    };
    return new Promise((resolve) =>
      setTimeout(() => resolve(RQFixedResponse), 1000),
    );
  },
  processReadyTransactions: async (
    requestPayload: any,
  ): Promise<IServerError | IResponse | any> => {
    try {
      const response: AxiosResponse<any> = await http.post<any>(
        `/v1/ready/`,
        // requestPayload.data
      );

      if (response.status === 201) {
        return response;
      } else if (response.status === 400) {
        return response.data;
      } else if (response.status === 401) {
        // window.location.href = '/logout';
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      if (axiosError && axiosError.status === 401) {
        requestPayload.navigateToLogout("/logout");
        return {
          status: false,
          message: "UnAuthrized",
        };
      } else if (axiosError && axiosError.status === 422) {
        return {
          status: false,
          message: `Errors : \n${format422Errors(axiosError?.response?.data)}`,
        };
      } else {
        return {
          status: false,
          message: errorMessage,
        };
      }
    }
  },
  deleteReadyTransactions: async (
    requestPayload: any,
  ): Promise<any | IServerError | IResponse | any> => {
    try {
      const response: AxiosResponse<any> = await http.delete<any>(
        `/v1/delete/`,
        // requestPayload
      );

      if (response.status === 201) {
        return response;
      } else if (response.status === 400) {
        return response.data;
      } else if (response.status === 401) {
        // window.location.href = '/logout';
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      if (axiosError && axiosError.status === 401) {
        requestPayload.navigateToLogout("/logout");
        return {
          status: false,
          message: "UnAuthrized",
        };
      } else if (axiosError && axiosError.status === 422) {
        return {
          status: false,
          message: `Errors : \n${format422Errors(axiosError?.response?.data)}`,
        };
      } else {
        return {
          status: false,
          message: errorMessage,
        };
      }
    }
  },
  deletePermanentReadyTransactions: async (
    requestPayload: any,
  ): Promise<any | IServerError | IResponse | any> => {
    try {
      const response: AxiosResponse<any> = await http.delete<any>(
        `/v1/permanent-delete/`,
        // requestPayload
      );

      if (response.status === 201) {
        return response;
      } else if (response.status === 400) {
        return response.data;
      } else if (response.status === 401) {
        // window.location.href = '/logout';
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      if (axiosError && axiosError.status === 401) {
        requestPayload.navigateToLogout("/logout");
        return {
          status: false,
          message: "UnAuthrized",
        };
      } else if (axiosError && axiosError.status === 422) {
        return {
          status: false,
          message: `Errors : \n${format422Errors(axiosError?.response?.data)}`,
        };
      } else {
        return {
          status: false,
          message: errorMessage,
        };
      }
    }
  },

  // #################################################### In Process Collection ######################################################
  getInProcessCollectionTransacitonsList: async (
    runGetListAPICall: boolean,
    client: string | null,
    clientType: string | null,
    type: string | null,
    dateAppliesOn: string | null,
    startDate: string | null,
    endDate: string | null,
    data: IGetListRequest,
    navigateToLogout: (path: string) => void,
  ): Promise<IServerError | IResponse | null | any> => {
    // if (!runGetListAPICall) return null;

    //     try {
    //       const response: AxiosResponse<any> =
    //         await http.get<any>(
    //           "/v1/salary-profile-report" +
    //             "&employee_status=" +
    //             employeeStatus +
    //             "&employee_id=" +
    //             employeeId +
    //             (dateRange && startDate ? "&start_date=" + startDate : "") +
    //             (dateRange && endDate ? "&end_date=" + endDate : "") +
    //             "&date_range=" +
    //             (dateRange ? "true" : "false") +
    //             "&search_query=" +
    //             data.search_query +
    //             "&order_by=" +
    //             data.order_by +
    //             "&order_direction=" +
    //             data.order_direction +
    //             "&page=" +
    //             data.page +
    //             "&size=" +
    //             data.size
    //         );

    //       if (response.status === 200) {
    //         return response.data;
    //       } else if (response.status === 400) {
    //         return {
    //           status: false,
    //           message: "Bad Request.",
    //           error: true,
    //         };
    //       } else if (response.status === 500) {
    //         return {
    //           status: false,
    //           message: "Something went wrong.",
    //           error: true,
    //         };
    //       }
    //       return {
    //         status: false,
    //         message: "Something went wrong please try again later.",
    //       };
    //     } catch (error) {
    //       const axiosError = error as AxiosError;
    //       console.error("An error occurred:", axiosError);
    //       const errorMessage =
    //         axiosError.response && typeof axiosError.response.data === "string"
    //           ? axiosError.response.data
    //           : "Unknown error";
    //       if (axiosError && axiosError.status === 401) {
    //         navigateToLogout("/logout");
    //         return {
    //           status: false,
    //           message: "UnAuthrized",
    //         };
    //       } else {
    //         return {
    //           status: false,
    //           message: errorMessage,
    //         };
    //       }
    //     }
    const RQFixedResponse: any = {
      page: 1,
      size: 50,
      total: 12,
      items: [
        {
          id: 1,
          ID: "13456789",
          party: "Zeeshan Technologies",
          type: "Payroll",
          amount: 1000,
          lastTab: "Ready",
          fromLastTabOn: "FEB 11 2026 08:30:25 AM",
          payPeriod: "Monthly",
          payPeriodEndDate: "Feb 15 2026",
          payPeriodPaymentDate: "Feb 13 2026",
          uploadDate: null,
          uploadTime: null,
          errorMessage: null,
          createdOn: "Feb 10 2025",
        },
        {
          id: 2,
          ID: "13456789",
          party: "Ahmad Technologies",
          type: "Payroll",
          amount: 1000,
          lastTab: "Ready",
          fromLastTabOn: "FEB 11 2026 08:30:25 AM",
          payPeriod: "Monthly",
          payPeriodEndDate: "Feb 15 2026",
          payPeriodPaymentDate: "Feb 13 2026",
          uploadDate: null,
          uploadTime: null,
          errorMessage: null,
          createdOn: "Feb 10 2025",
        },
        {
          id: 3,
          ID: "13456789",
          party: "Umair Technologies",
          type: "Payroll",
          amount: 1000,
          lastTab: "Ready",
          fromLastTabOn: "FEB 11 2026 08:30:25 AM",
          payPeriod: "Monthly",
          payPeriodEndDate: "Feb 15 2026",
          payPeriodPaymentDate: "Feb 13 2026",
          uploadDate: null,
          uploadTime: null,
          errorMessage: null,
          createdOn: "Feb 10 2025",
        },
        {
          id: 4,
          ID: "13456789",
          party: "Qasim Technologies",
          type: "Payroll",
          amount: 1000,
          lastTab: "Ready",
          fromLastTabOn: "FEB 11 2026 08:30:25 AM",
          payPeriod: "Monthly",
          payPeriodEndDate: "Feb 15 2026",
          payPeriodPaymentDate: "Feb 13 2026",
          uploadDate: null,
          uploadTime: null,
          errorMessage: null,
          createdOn: "Feb 10 2025",
        },
      ],
      keyField: [
        {
          field: "id",
          templateUrl: "/rq-remittance-report/details/id",
        },
      ],
      headerColumns: [
        {
          field: "ID",
          headerName: "ID",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 0,
          frozenColumn: false,
          footer: null,
          minWidth: 50,
          maxWidth: null,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "party",
          headerName: "Party",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 1,
          frozenColumn: false,
          footer: null,
          minWidth: 120,
          maxWidth: null,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "type",
          headerName: "Type",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 2,
          frozenColumn: false,
          footer: null,
          minWidth: 120,
          maxWidth: null,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "amount",
          headerName: "Amount",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 3,
          frozenColumn: false,
          footer: null,
          minWidth: 120,
          maxWidth: null,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "lastTab",
          headerName: "Last Tab",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 4,
          frozenColumn: false,
          footer: null,
          minWidth: 120,
          maxWidth: null,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "fromLastTabOn",
          headerName: "From Last Tab on",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 5,
          frozenColumn: false,
          footer: null,
          minWidth: 120,
          maxWidth: null,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "payPeriod",
          headerName: "Pay Period",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 6,
          frozenColumn: false,
          footer: null,
          minWidth: 120,
          maxWidth: null,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "payPeriodEndDate",
          headerName: "Pay Period End Date",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 7,
          frozenColumn: false,
          footer: null,
          minWidth: 120,
          maxWidth: null,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "payPeriodPaymentDate",
          headerName: "Pay Period Payment Date",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 8,
          frozenColumn: false,
          footer: null,
          minWidth: 120,
          maxWidth: null,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "uploadDate",
          headerName: "Upload Date",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 9,
          frozenColumn: false,
          footer: null,
          minWidth: 200,
          maxWidth: null,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "uploadTime",
          headerName: "Upload Time",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 10,
          frozenColumn: false,
          footer: null,
          minWidth: 200,
          maxWidth: null,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "errorMessage",
          headerName: "Error Message",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 11,
          frozenColumn: false,
          footer: null,
          minWidth: 200,
          maxWidth: null,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "createdOn",
          headerName: "Created On",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 12,
          frozenColumn: false,
          footer: null,
          minWidth: 120,
          maxWidth: null,
          fieldName: null,
          edr: "edr",
        },
      ],
    };
    return new Promise((resolve) =>
      setTimeout(() => resolve(RQFixedResponse), 1000),
    );
  },
  saveInProcessCollectionTransactions: async (
    requestPayload: any,
  ): Promise<IServerError | IResponse | any> => {
    try {
      const response: AxiosResponse<any> = await http.post<any>(
        `/v1/ready/`,
        // requestPayload.data
      );

      if (response.status === 201) {
        return response;
      } else if (response.status === 400) {
        return response.data;
      } else if (response.status === 401) {
        // window.location.href = '/logout';
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      if (axiosError && axiosError.status === 401) {
        requestPayload.navigateToLogout("/logout");
        return {
          status: false,
          message: "UnAuthrized",
        };
      } else if (axiosError && axiosError.status === 422) {
        return {
          status: false,
          message: `Errors : \n${format422Errors(axiosError?.response?.data)}`,
        };
      } else {
        return {
          status: false,
          message: errorMessage,
        };
      }
    }
  },
  deleteInProcessCollectionTransactions: async (
    requestPayload: any,
  ): Promise<any | IServerError | IResponse | any> => {
    try {
      const response: AxiosResponse<any> = await http.delete<any>(
        `/v1/delete/`,
        // requestPayload
      );

      if (response.status === 201) {
        return response;
      } else if (response.status === 400) {
        return response.data;
      } else if (response.status === 401) {
        // window.location.href = '/logout';
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      if (axiosError && axiosError.status === 401) {
        requestPayload.navigateToLogout("/logout");
        return {
          status: false,
          message: "UnAuthrized",
        };
      } else if (axiosError && axiosError.status === 422) {
        return {
          status: false,
          message: `Errors : \n${format422Errors(axiosError?.response?.data)}`,
        };
      } else {
        return {
          status: false,
          message: errorMessage,
        };
      }
    }
  },
  updatePayPeriodPaymentDateInProcessCollectionTransactions: async (
    rowData: any,
  ): Promise<any | IServerError | IResponse | any> => {
    try {
      const response: AxiosResponse<any> = await http.put<any>(
        `/v1/direct-deposit/collections-data/update-payment-date`,
        rowData,
      );

      if (response.status === 201) {
        return response;
      } else if (response.status === 400) {
        return response.data;
      } else if (response.status === 401) {
        // window.location.href = '/logout';
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      return {
        status: false,
        message: errorMessage,
      };
    }
  },
  saveErrorMessageInProcessCollectionTransactions: async (
    loadedMessageId: any,
    requestPayload: any,
  ): Promise<any | IServerError | IResponse | any> => {
    if (loadedMessageId) {
      try {
        const response: AxiosResponse<any> = await http.put<any>(
          `/v1/error-messages/${loadedMessageId}/`,
          requestPayload,
        );

        if (response.status === 200) {
          return response;
        } else if (response.status === 400) {
          return response.data;
        } else if (response.status === 401) {
          // window.location.href = '/logout';
        } else if (response.status === 500) {
          return {
            status: false,
            message: "Something went wrong.",
            error: true,
          };
        }

        return {
          status: false,
          message: "Something went wrong please try again later.",
        };
      } catch (error) {
        const axiosError = error as AxiosError;
        console.error("An error occurred:", axiosError);
        const errorMessage =
          axiosError.response && typeof axiosError.response.data === "string"
            ? axiosError.response.data
            : "Unknown error";
        return {
          status: false,
          message: errorMessage,
        };
      }
    }
    try {
      const response: AxiosResponse<any> = await http.post<any>(
        `/v1/error-messages/`,
        requestPayload,
      );

      if (response.status === 201) {
        return response;
      } else if (response.status === 400) {
        return response.data;
      } else if (response.status === 401) {
        // window.location.href = '/logout';
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      return {
        status: false,
        message: errorMessage,
      };
    }
  },
  getERrorMessageById: async (
    id: string | undefined,
    navigateToLogout: (path: string) => void,
  ): Promise<IServerError | IResponse | null | any> => {
    if (!id) return null;
    try {
      const response: AxiosResponse<any> = await http.get<any>(
        `/v1/error-messages/${id}/`,
      );

      if (response.status === 200) {
        return response.data;
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      if (axiosError && axiosError.status === 401) {
        navigateToLogout("/logout");
        return {
          status: false,
          message: "UnAuthrized",
        };
      } else {
        return {
          status: false,
          message: errorMessage,
        };
      }
    }
  },
  createDebitFileInProcessCollectionTransactions: async (
    requestPayload: any,
  ): Promise<any | IServerError | IResponse | any> => {
    try {
      // const response: AxiosResponse<any> = await http.post<any>(
      //   `/v1/save`,
      //   // requestPayload
      // );

      // const url = `/v1/direct-deposit-file/${id}/`;
      // const response: any = await http.get(url, {
      //   responseType: "arraybuffer",
      // });
      // const response = await http.post(url)

      // const response: AxiosResponse<any> = await http.get<any>(
      //  `/v1/direct-deposit-file/${id}/`
      // );
      // if (response.status === 200) {
      //   return response;
      // } else if (response.status === 201) {
      //   return response.data;
      // } else if (response.status === 400) {
      //   const text = new TextDecoder().decode(response.data);
      //   const responseObject = JSON.parse(text);
      //   let response400 = {
      //     ...response,
      //     data: responseObject,
      //   };
      //   return response400;
      // } else if (response.status === 500) {

      const url = `/v1/direct-deposit/debit-file`;
      const response: any = await http.post(url, requestPayload, {
        responseType: "arraybuffer",
      });
      // const response = await http.post(url)
      if (response.status === 201) {
        return response.data;
      } else if (response.status === 400) {
        const text = new TextDecoder().decode(response.data);
        const responseObject = JSON.parse(text);
        let response400 = {
          ...response,
          data: responseObject,
        };
        return response400;
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      return {
        status: false,
        message: errorMessage,
      };
    }
  },
  createDebitFileCollectionUploadedTransactions: async (
    id: any,
  ): Promise<any | IServerError | IResponse | any> => {
    try {
      // const response: AxiosResponse<any> = await http.post<any>(
      //   `/v1/save`,
      //   // requestPayload
      // );

      // const url = `/v1/direct-deposit-file/${id}/`;
      // const response: any = await http.get(url, {
      //   responseType: "arraybuffer",
      // });
      // const response = await http.post(url)

      const response: AxiosResponse<any> = await http.get<any>(
        `/v1/direct-deposit-file/${id}/`,
      );
      if (response.status === 200) {
        return response;
      } else if (response.status === 201) {
        return response.data;
      } else if (response.status === 400) {
        const text = new TextDecoder().decode(response.data);
        const responseObject = JSON.parse(text);
        let response400 = {
          ...response,
          data: responseObject,
        };
        return response400;
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      return {
        status: false,
        message: errorMessage,
      };
    }
  },
  undoInProcessCollectionTransactions: async (
    requestPayload: any,
  ): Promise<any | IServerError | IResponse | any> => {
    try {
      const response: AxiosResponse<any> = await http.post<any>(
        `/v1/undo`,
        // requestPayload
      );

      if (response.status === 201) {
        return response;
      } else if (response.status === 400) {
        return response.data;
      } else if (response.status === 401) {
        // window.location.href = '/logout';
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      return {
        status: false,
        message: errorMessage,
      };
    }
  },
  uploadInProcessCollectionTransactions: async (
    requestPayload: any,
  ): Promise<any | IServerError | IResponse | any> => {
    try {
      const response: AxiosResponse<any> = await http.post<any>(
        `/v1/upload`,
        // requestPayload
      );

      if (response.status === 201) {
        return response;
      } else if (response.status === 400) {
        return response.data;
      } else if (response.status === 401) {
        // window.location.href = '/logout';
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      return {
        status: false,
        message: errorMessage,
      };
    }
  },
  errorInTransactionInProcessCollectionTransactions: async (
    requestPayload: any,
  ): Promise<any | IServerError | IResponse | any> => {
    try {
      const response: AxiosResponse<any> = await http.post<any>(
        `/v1/error-in-transaction`,
        // requestPayload
      );

      if (response.status === 201) {
        return response;
      } else if (response.status === 400) {
        return response.data;
      } else if (response.status === 401) {
        // window.location.href = '/logout';
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      return {
        status: false,
        message: errorMessage,
      };
    }
  },
  getErrorMessageDDL: async (): Promise<
    any | IServerError | IResponse | null
  > => {
    const response: AxiosResponse<any[]> = await http.get<any[]>(
      `/v1/error-messages/ddl`,
    );

    if (response.status === 200) {
      return response.data;
    }

    return {
      status: false,
      message: "Something went wrong please try again later.",
    };
  },

  // #################################################### In Collection ######################################################
  getInCollectionTransacitonsList: async (
    runGetListAPICall: boolean,
    client: string | null,
    clientType: string | null,
    type: string | null,
    dateAppliesOn: string | null,
    startDate: string | null,
    endDate: string | null,
    data: IGetListRequest,
    navigateToLogout: (path: string) => void,
  ): Promise<IServerError | IResponse | null | any> => {
    // if (!runGetListAPICall) return null;

    //     try {
    //       const response: AxiosResponse<any> =
    //         await http.get<any>(
    //           "/v1/salary-profile-report" +
    //             "&employee_status=" +
    //             employeeStatus +
    //             "&employee_id=" +
    //             employeeId +
    //             (dateRange && startDate ? "&start_date=" + startDate : "") +
    //             (dateRange && endDate ? "&end_date=" + endDate : "") +
    //             "&date_range=" +
    //             (dateRange ? "true" : "false") +
    //             "&search_query=" +
    //             data.search_query +
    //             "&order_by=" +
    //             data.order_by +
    //             "&order_direction=" +
    //             data.order_direction +
    //             "&page=" +
    //             data.page +
    //             "&size=" +
    //             data.size
    //         );

    //       if (response.status === 200) {
    //         return response.data;
    //       } else if (response.status === 400) {
    //         return {
    //           status: false,
    //           message: "Bad Request.",
    //           error: true,
    //         };
    //       } else if (response.status === 500) {
    //         return {
    //           status: false,
    //           message: "Something went wrong.",
    //           error: true,
    //         };
    //       }
    //       return {
    //         status: false,
    //         message: "Something went wrong please try again later.",
    //       };
    //     } catch (error) {
    //       const axiosError = error as AxiosError;
    //       console.error("An error occurred:", axiosError);
    //       const errorMessage =
    //         axiosError.response && typeof axiosError.response.data === "string"
    //           ? axiosError.response.data
    //           : "Unknown error";
    //       if (axiosError && axiosError.status === 401) {
    //         navigateToLogout("/logout");
    //         return {
    //           status: false,
    //           message: "UnAuthrized",
    //         };
    //       } else {
    //         return {
    //           status: false,
    //           message: errorMessage,
    //         };
    //       }
    //     }
    const RQFixedResponse: any = {
      page: 1,
      size: 50,
      total: 12,
      items: [
        {
          id: 1,
          ID: "13456789",
          party: "Zeeshan Technologies",
          type: "Payroll",
          amount: 1000,
          lastTab: "Ready",
          fromLastTabOn: "FEB 11 2026 08:30:25 AM",
          payPeriod: "Monthly",
          payPeriodEndDate: "Feb 15 2026",
          payPeriodPaymentDate: "Feb 13 2026",
          uploadDate: null,
          uploadTime: null,
          errorMessage: null,
          createdOn: "Feb 10 2025",
          uploadDateAndTime: "Feb 18 2026 10:10 AM",
        },
        {
          id: 2,
          ID: "13456789",
          party: "Ahmad Technologies",
          type: "Payroll",
          amount: 1000,
          lastTab: "Ready",
          fromLastTabOn: "FEB 11 2026 08:30:25 AM",
          payPeriod: "Monthly",
          payPeriodEndDate: "Feb 15 2026",
          payPeriodPaymentDate: "Feb 13 2026",
          uploadDate: null,
          uploadTime: null,
          errorMessage: null,
          createdOn: "Feb 10 2025",
          uploadDateAndTime: "Feb 18 2026 10:10 AM",
        },
        {
          id: 3,
          ID: "13456789",
          party: "Umair Technologies",
          type: "Payroll",
          amount: 1000,
          lastTab: "Ready",
          fromLastTabOn: "FEB 11 2026 08:30:25 AM",
          payPeriod: "Monthly",
          payPeriodEndDate: "Feb 15 2026",
          payPeriodPaymentDate: "Feb 13 2026",
          uploadDate: null,
          uploadTime: null,
          errorMessage: null,
          createdOn: "Feb 10 2025",
          uploadDateAndTime: "Feb 18 2026 10:10 AM",
        },
        {
          id: 4,
          ID: "13456789",
          party: "Qasim Technologies",
          type: "Payroll",
          amount: 1000,
          lastTab: "Ready",
          fromLastTabOn: "FEB 11 2026 08:30:25 AM",
          payPeriod: "Monthly",
          payPeriodEndDate: "Feb 15 2026",
          payPeriodPaymentDate: "Feb 13 2026",
          uploadDate: null,
          uploadTime: null,
          uploadDateAndTime: "Feb 18 2026 10:10 AM",
          errorMessage: null,
          createdOn: "Feb 10 2025",
        },
      ],
      keyField: [
        {
          field: "id",
          templateUrl: "/rq-remittance-report/details/id",
        },
      ],
      headerColumns: [
        {
          field: "ID",
          headerName: "ID",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 0,
          frozenColumn: false,
          footer: null,
          minWidth: 50,
          maxWidth: null,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "party",
          headerName: "Party",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 1,
          frozenColumn: false,
          footer: null,
          minWidth: 120,
          maxWidth: null,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "type",
          headerName: "Type",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 2,
          frozenColumn: false,
          footer: null,
          minWidth: 120,
          maxWidth: null,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "amount",
          headerName: "Amount",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 3,
          frozenColumn: false,
          footer: null,
          minWidth: 120,
          maxWidth: null,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "lastTab",
          headerName: "Last Tab",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 4,
          frozenColumn: false,
          footer: null,
          minWidth: 120,
          maxWidth: null,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "fromLastTabOn",
          headerName: "From Last Tab on",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 5,
          frozenColumn: false,
          footer: null,
          minWidth: 120,
          maxWidth: null,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "payPeriod",
          headerName: "Pay Period",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 6,
          frozenColumn: false,
          footer: null,
          minWidth: 120,
          maxWidth: null,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "payPeriodEndDate",
          headerName: "Pay Period End Date",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 7,
          frozenColumn: false,
          footer: null,
          minWidth: 120,
          maxWidth: null,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "payPeriodPaymentDate",
          headerName: "Pay Period Payment Date",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 8,
          frozenColumn: false,
          footer: null,
          minWidth: 120,
          maxWidth: null,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "uploadDateAndTime",
          headerName: "Upload Date and Time",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 10,
          frozenColumn: false,
          footer: null,
          minWidth: 200,
          maxWidth: null,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "errorMessage",
          headerName: "Error Message",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 11,
          frozenColumn: false,
          footer: null,
          minWidth: 200,
          maxWidth: null,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "createdOn",
          headerName: "Created On",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 12,
          frozenColumn: false,
          footer: null,
          minWidth: 120,
          maxWidth: null,
          fieldName: null,
          edr: "edr",
        },
      ],
    };
    return new Promise((resolve) =>
      setTimeout(() => resolve(RQFixedResponse), 1000),
    );
  },
  saveInCollectionTransactions: async (
    requestPayload: any,
  ): Promise<IServerError | IResponse | any> => {
    try {
      const response: AxiosResponse<any> = await http.post<any>(
        `/v1/ready/`,
        // requestPayload.data
      );

      if (response.status === 201) {
        return response;
      } else if (response.status === 400) {
        return response.data;
      } else if (response.status === 401) {
        // window.location.href = '/logout';
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      if (axiosError && axiosError.status === 401) {
        requestPayload.navigateToLogout("/logout");
        return {
          status: false,
          message: "UnAuthrized",
        };
      } else if (axiosError && axiosError.status === 422) {
        return {
          status: false,
          message: `Errors : \n${format422Errors(axiosError?.response?.data)}`,
        };
      } else {
        return {
          status: false,
          message: errorMessage,
        };
      }
    }
  },
  deleteInCollectionTransactions: async (
    requestPayload: any,
  ): Promise<any | IServerError | IResponse | any> => {
    try {
      const response: AxiosResponse<any> = await http.delete<any>(
        `/v1/delete/`,
        // requestPayload
      );

      if (response.status === 201) {
        return response;
      } else if (response.status === 400) {
        return response.data;
      } else if (response.status === 401) {
        // window.location.href = '/logout';
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      if (axiosError && axiosError.status === 401) {
        requestPayload.navigateToLogout("/logout");
        return {
          status: false,
          message: "UnAuthrized",
        };
      } else if (axiosError && axiosError.status === 422) {
        return {
          status: false,
          message: `Errors : \n${format422Errors(axiosError?.response?.data)}`,
        };
      } else {
        return {
          status: false,
          message: errorMessage,
        };
      }
    }
  },
  updatePayPeriodPaymentDateInCollectionTransactions: async (
    date: string | null,
  ): Promise<any | IServerError | IResponse | any> => {
    if (!date) return null;
    try {
      const response: AxiosResponse<any> = await http.put<any>(
        `/v1/pay-period-payment-date/?date=${date}`,
        // requestPayload
      );

      if (response.status === 201) {
        return response;
      } else if (response.status === 400) {
        return response.data;
      } else if (response.status === 401) {
        // window.location.href = '/logout';
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      return {
        status: false,
        message: errorMessage,
      };
    }
  },
  saveErrorMessageInCollectionTransactions: async (
    requestPayload: any,
  ): Promise<any | IServerError | IResponse | any> => {
    try {
      const response: AxiosResponse<any> = await http.post<any>(
        `/v1/save-error-message/?date`,
        // requestPayload
      );

      if (response.status === 201) {
        return response;
      } else if (response.status === 400) {
        return response.data;
      } else if (response.status === 401) {
        // window.location.href = '/logout';
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      return {
        status: false,
        message: errorMessage,
      };
    }
  },
  createSelectNonRedsFileInCollectionTransactions: async (
    requestPayload: any,
  ): Promise<any | IServerError | IResponse | any> => {
    try {
      const response: AxiosResponse<any> = await http.post<any>(
        `/v1/save`, // change url later
        // requestPayload
      );

      if (response.status === 201) {
        return response;
      } else if (response.status === 400) {
        return response.data;
      } else if (response.status === 401) {
        // window.location.href = '/logout';
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      return {
        status: false,
        message: errorMessage,
      };
    }
  },
  undoInCollectionTransactions: async (
    requestPayload: any,
  ): Promise<any | IServerError | IResponse | any> => {
    try {
      const response: AxiosResponse<any> = await http.post<any>(
        `/v1/undo`,
        // requestPayload
      );

      if (response.status === 201) {
        return response;
      } else if (response.status === 400) {
        return response.data;
      } else if (response.status === 401) {
        // window.location.href = '/logout';
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      return {
        status: false,
        message: errorMessage,
      };
    }
  },
  amountCollectedInCollectionTransactions: async (
    requestPayload: any,
  ): Promise<any | IServerError | IResponse | any> => {
    try {
      const response: AxiosResponse<any> = await http.post<any>(
        `/v1/upload`, // change url later
        // requestPayload
      );

      if (response.status === 201) {
        return response;
      } else if (response.status === 400) {
        return response.data;
      } else if (response.status === 401) {
        // window.location.href = '/logout';
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      return {
        status: false,
        message: errorMessage,
      };
    }
  },
  errorInTransactionInCollectionTransactions: async (
    requestPayload: any,
  ): Promise<any | IServerError | IResponse | any> => {
    try {
      const response: AxiosResponse<any> = await http.post<any>(
        `/v1/error-in-transaction`,
        // requestPayload
      );

      if (response.status === 201) {
        return response;
      } else if (response.status === 400) {
        return response.data;
      } else if (response.status === 401) {
        // window.location.href = '/logout';
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      return {
        status: false,
        message: errorMessage,
      };
    }
  },

  // #################################################### In Process Payment ######################################################
  getInProcessPaymentTransacitonsList: async (
    runGetListAPICall: boolean,
    client: string | null,
    clientType: string | null,
    type: string | null,
    dateAppliesOn: string | null,
    startDate: string | null,
    endDate: string | null,
    data: IGetListRequest,
    navigateToLogout: (path: string) => void,
  ): Promise<IServerError | IResponse | null | any> => {
    // if (!runGetListAPICall) return null;

    //     try {
    //       const response: AxiosResponse<any> =
    //         await http.get<any>(
    //           "/v1/salary-profile-report" +
    //             "&employee_status=" +
    //             employeeStatus +
    //             "&employee_id=" +
    //             employeeId +
    //             (dateRange && startDate ? "&start_date=" + startDate : "") +
    //             (dateRange && endDate ? "&end_date=" + endDate : "") +
    //             "&date_range=" +
    //             (dateRange ? "true" : "false") +
    //             "&search_query=" +
    //             data.search_query +
    //             "&order_by=" +
    //             data.order_by +
    //             "&order_direction=" +
    //             data.order_direction +
    //             "&page=" +
    //             data.page +
    //             "&size=" +
    //             data.size
    //         );

    //       if (response.status === 200) {
    //         return response.data;
    //       } else if (response.status === 400) {
    //         return {
    //           status: false,
    //           message: "Bad Request.",
    //           error: true,
    //         };
    //       } else if (response.status === 500) {
    //         return {
    //           status: false,
    //           message: "Something went wrong.",
    //           error: true,
    //         };
    //       }
    //       return {
    //         status: false,
    //         message: "Something went wrong please try again later.",
    //       };
    //     } catch (error) {
    //       const axiosError = error as AxiosError;
    //       console.error("An error occurred:", axiosError);
    //       const errorMessage =
    //         axiosError.response && typeof axiosError.response.data === "string"
    //           ? axiosError.response.data
    //           : "Unknown error";
    //       if (axiosError && axiosError.status === 401) {
    //         navigateToLogout("/logout");
    //         return {
    //           status: false,
    //           message: "UnAuthrized",
    //         };
    //       } else {
    //         return {
    //           status: false,
    //           message: errorMessage,
    //         };
    //       }
    //     }
    const RQFixedResponse: any = {
      page: 1,
      size: 50,
      total: 12,
      items: [
        {
          id: 1,
          ID: "13456789",
          party: "Zeeshan Technologies",
          type: "Payroll",
          amount: 1000,
          lastTab: "Ready",
          fromLastTabOn: "FEB 11 2026 08:30:25 AM",
          payPeriod: "Monthly",
          payPeriodEndDate: "Feb 15 2026",
          payPeriodPaymentDate: "Feb 13 2026",
          uploadDate: null,
          uploadTime: null,
          errorMessage: null,
          createdOn: "Feb 10 2025",
          orders: [
            {
              ID: "13456789",
              party: "AJAY",
              type: "Payroll",
              amount: 1000,
              lastTab: "Ready",
              fromLastTabOn: "FEB 11 2026 08:30:25 AM",
              payPeriod: "Monthly",
              payPeriodEndDate: "Feb 15 2026",
              payPeriodPaymentDate: "Feb 13 2026",
              uploadDate: null,
              uploadTime: null,
              errorMessage: null,
              createdOn: "Feb 10 2025",
            },
            {
              ID: "13456789",
              party: "DEV",
              type: "Payroll",
              amount: 1000,
              lastTab: "Ready",
              fromLastTabOn: "FEB 11 2026 08:30:25 AM",
              payPeriod: "Monthly",
              payPeriodEndDate: "Feb 15 2026",
              payPeriodPaymentDate: "Feb 13 2026",
              uploadDate: null,
              uploadTime: null,
              errorMessage: null,
              createdOn: "Feb 10 2025",
            },
          ],
        },
        {
          id: 2,
          ID: "13456789",
          party: "Ahmad Technologies",
          type: "Payroll",
          amount: 1000,
          lastTab: "Ready",
          fromLastTabOn: "FEB 11 2026 08:30:25 AM",
          payPeriod: "Monthly",
          payPeriodEndDate: "Feb 15 2026",
          payPeriodPaymentDate: "Feb 13 2026",
          uploadDate: null,
          uploadTime: null,
          errorMessage: null,
          createdOn: "Feb 10 2025",
          orders: [
            {
              ID: "13456789",
              party: "AJAY",
              type: "Payroll",
              amount: 1000,
              lastTab: "Ready",
              fromLastTabOn: "FEB 11 2026 08:30:25 AM",
              payPeriod: "Monthly",
              payPeriodEndDate: "Feb 15 2026",
              payPeriodPaymentDate: "Feb 13 2026",
              uploadDate: null,
              uploadTime: null,
              errorMessage: null,
              createdOn: "Feb 10 2025",
            },
            {
              ID: "13456789",
              party: "DEV",
              type: "Payroll",
              amount: 1000,
              lastTab: "Ready",
              fromLastTabOn: "FEB 11 2026 08:30:25 AM",
              payPeriod: "Monthly",
              payPeriodEndDate: "Feb 15 2026",
              payPeriodPaymentDate: "Feb 13 2026",
              uploadDate: null,
              uploadTime: null,
              errorMessage: null,
              createdOn: "Feb 10 2025",
            },
          ],
        },
        {
          id: 3,
          ID: "13456789",
          party: "Umair Technologies",
          type: "Payroll",
          amount: 1000,
          lastTab: "Ready",
          fromLastTabOn: "FEB 11 2026 08:30:25 AM",
          payPeriod: "Monthly",
          payPeriodEndDate: "Feb 15 2026",
          payPeriodPaymentDate: "Feb 13 2026",
          uploadDate: null,
          uploadTime: null,
          errorMessage: null,
          createdOn: "Feb 10 2025",
        },
        {
          id: 4,
          ID: "13456789",
          party: "Qasim Technologies",
          type: "Payroll",
          amount: 1000,
          lastTab: "Ready",
          fromLastTabOn: "FEB 11 2026 08:30:25 AM",
          payPeriod: "Monthly",
          payPeriodEndDate: "Feb 15 2026",
          payPeriodPaymentDate: "Feb 13 2026",
          uploadDate: null,
          uploadTime: null,
          errorMessage: null,
          createdOn: "Feb 10 2025",
        },
      ],
      keyField: [
        {
          field: "id",
          templateUrl: "/rq-remittance-report/details/id",
        },
      ],
      headerColumns: [
        {
          field: "ID",
          headerName: "ID",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 0,
          frozenColumn: false,
          footer: null,
          minWidth: 100,
          maxWidth: 100,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "party",
          headerName: "Party",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 1,
          frozenColumn: false,
          footer: null,
          minWidth: 200,
          maxWidth: 200,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "type",
          headerName: "Type",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 2,
          frozenColumn: false,
          footer: null,
          minWidth: 120,
          maxWidth: 120,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "amount",
          headerName: "Amount",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 3,
          frozenColumn: false,
          footer: null,
          minWidth: 120,
          maxWidth: 120,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "lastTab",
          headerName: "Last Tab",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 4,
          frozenColumn: false,
          footer: null,
          minWidth: 120,
          maxWidth: 120,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "fromLastTabOn",
          headerName: "From Last Tab on",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 5,
          frozenColumn: false,
          footer: null,
          minWidth: 150,
          maxWidth: 150,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "payPeriod",
          headerName: "Pay Period",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 6,
          frozenColumn: false,
          footer: null,
          minWidth: 120,
          maxWidth: 120,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "payPeriodEndDate",
          headerName: "Pay Period End Date",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 7,
          frozenColumn: false,
          footer: null,
          minWidth: 180,
          maxWidth: 180,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "payPeriodPaymentDate",
          headerName: "Pay Period Payment Date",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 8,
          frozenColumn: false,
          footer: null,
          minWidth: 200,
          maxWidth: 200,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "uploadDate",
          headerName: "Upload Date",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 9,
          frozenColumn: false,
          footer: null,
          minWidth: 200,
          maxWidth: 120,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "uploadTime",
          headerName: "Upload Time",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 10,
          frozenColumn: false,
          footer: null,
          minWidth: 200,
          maxWidth: 120,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "errorMessage",
          headerName: "Error Message",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 11,
          frozenColumn: false,
          footer: null,
          minWidth: 200,
          maxWidth: 200,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "createdOn",
          headerName: "Created On",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 12,
          frozenColumn: false,
          footer: null,
          minWidth: 120,
          maxWidth: 120,
          fieldName: null,
          edr: "edr",
        },
      ],
    };
    return new Promise((resolve) =>
      setTimeout(() => resolve(RQFixedResponse), 1000),
    );
  },
  saveInProcessPaymentTransactions: async (
    requestPayload: any,
  ): Promise<IServerError | IResponse | any> => {
    try {
      const response: AxiosResponse<any> = await http.post<any>(
        `/v1/save/`,
        // requestPayload.data
      );

      if (response.status === 201) {
        return response;
      } else if (response.status === 400) {
        return response.data;
      } else if (response.status === 401) {
        // window.location.href = '/logout';
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      if (axiosError && axiosError.status === 401) {
        requestPayload.navigateToLogout("/logout");
        return {
          status: false,
          message: "UnAuthrized",
        };
      } else if (axiosError && axiosError.status === 422) {
        return {
          status: false,
          message: `Errors : \n${format422Errors(axiosError?.response?.data)}`,
        };
      } else {
        return {
          status: false,
          message: errorMessage,
        };
      }
    }
  },
  deleteInProcessPaymentTransactions: async (
    requestPayload: any,
  ): Promise<any | IServerError | IResponse | any> => {
    try {
      const response: AxiosResponse<any> = await http.delete<any>(
        `/v1/delete/`,
        // requestPayload
      );

      if (response.status === 201) {
        return response;
      } else if (response.status === 400) {
        return response.data;
      } else if (response.status === 401) {
        // window.location.href = '/logout';
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      if (axiosError && axiosError.status === 401) {
        requestPayload.navigateToLogout("/logout");
        return {
          status: false,
          message: "UnAuthrized",
        };
      } else if (axiosError && axiosError.status === 422) {
        return {
          status: false,
          message: `Errors : \n${format422Errors(axiosError?.response?.data)}`,
        };
      } else {
        return {
          status: false,
          message: errorMessage,
        };
      }
    }
  },
  createPaymentFileInProcessPaymentTransactions: async (
    requestPayload: any,
  ): Promise<any | IServerError | IResponse | any> => {
    try {
      // const response: AxiosResponse<any> = await http.post<any>(
      //   `/v1/save`,
      //   // requestPayload
      // );

      // const url = `/v1/direct-deposit-file/${id}/credit/`;
      // const response: any = await http.post(url, {
      //   responseType: "arraybuffer",
      // });
      // const response = await http.post(url)
      //  const response: AxiosResponse<any> = await http.get<any>(
      //  `/v1/direct-deposit-file/${id}/credit/`
      // );
      const url = `/v1/direct-deposit/credit-file/`;
      const response: any = await http.post(url, requestPayload, {
        responseType: "arraybuffer",
      });
      if (response.status === 200) {
        return response;
      } else if (response.status === 201) {
        return response.data;
      } else if (response.status === 400) {
        const text = new TextDecoder().decode(response.data);
        const responseObject = JSON.parse(text);
        let response400 = {
          ...response,
          data: responseObject,
        };
        return response400;
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      return {
        status: false,
        message: errorMessage,
      };
    }
  },
  downloadPaymentFileInProcessPaymentTransactions: async (
    id: any,
  ): Promise<any | IServerError | IResponse | any> => {
    try {
      // const response: AxiosResponse<any> = await http.post<any>(
      //   `/v1/save`,
      //   // requestPayload
      // );

      // const url = `/v1/direct-deposit-file/${id}/credit/`;
      // const response: any = await http.post(url, {
      //   responseType: "arraybuffer",
      // });
      // const response = await http.post(url)
      //  const response: AxiosResponse<any> = await http.get<any>(
      //  `/v1/direct-deposit-file/${id}/credit/`
      // );
      const response: AxiosResponse<any> = await http.get<any>(
        `/v1/direct-deposit-file/${id}/credit/`,
      );
      if (response.status === 200) {
        return response;
      } else if (response.status === 201) {
        return response.data;
      } else if (response.status === 400) {
        const text = new TextDecoder().decode(response.data);
        const responseObject = JSON.parse(text);
        let response400 = {
          ...response,
          data: responseObject,
        };
        return response400;
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      return {
        status: false,
        message: errorMessage,
      };
    }
  },
  undoInProcessPaymentTransactions: async (
    requestPayload: any,
  ): Promise<any | IServerError | IResponse | any> => {
    try {
      const response: AxiosResponse<any> = await http.post<any>(
        `/v1/undo`,
        // requestPayload
      );

      if (response.status === 201) {
        return response;
      } else if (response.status === 400) {
        return response.data;
      } else if (response.status === 401) {
        // window.location.href = '/logout';
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      return {
        status: false,
        message: errorMessage,
      };
    }
  },
  proccessedInProcessPaymentTransactions: async (
    requestPayload: any,
  ): Promise<any | IServerError | IResponse | any> => {
    try {
      const response: AxiosResponse<any> = await http.post<any>(
        `/v1/upload`,
        // requestPayload
      );

      if (response.status === 201) {
        return response;
      } else if (response.status === 400) {
        return response.data;
      } else if (response.status === 401) {
        // window.location.href = '/logout';
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      return {
        status: false,
        message: errorMessage,
      };
    }
  },
  errorInTransactionInProcessPaymentTransactions: async (
    requestPayload: any,
  ): Promise<any | IServerError | IResponse | any> => {
    try {
      const response: AxiosResponse<any> = await http.post<any>(
        `/v1/error-in-transaction`,
        // requestPayload
      );

      if (response.status === 201) {
        return response;
      } else if (response.status === 400) {
        return response.data;
      } else if (response.status === 401) {
        // window.location.href = '/logout';
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      return {
        status: false,
        message: errorMessage,
      };
    }
  },
  saveErrorMessageInProcessPaymentTransactions: async (
    requestPayload: any,
  ): Promise<any | IServerError | IResponse | any> => {
    try {
      const response: AxiosResponse<any> = await http.post<any>(
        `/v1/save-error-message/?date`,
        // requestPayload
      );

      if (response.status === 201) {
        return response;
      } else if (response.status === 400) {
        return response.data;
      } else if (response.status === 401) {
        // window.location.href = '/logout';
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      return {
        status: false,
        message: errorMessage,
      };
    }
  },

  // #################################################### Completed Collection ######################################################
  getCompletedCollectionTransacitonsList: async (
    runGetListAPICall: boolean,
    client: string | null,
    clientType: string | null,
    type: string | null,
    dateAppliesOn: string | null,
    startDate: string | null,
    endDate: string | null,
    data: IGetListRequest,
    navigateToLogout: (path: string) => void,
  ): Promise<IServerError | IResponse | null | any> => {
    // if (!runGetListAPICall) return null;

    //     try {
    //       const response: AxiosResponse<any> =
    //         await http.get<any>(
    //           "/v1/salary-profile-report" +
    //             "&employee_status=" +
    //             employeeStatus +
    //             "&employee_id=" +
    //             employeeId +
    //             (dateRange && startDate ? "&start_date=" + startDate : "") +
    //             (dateRange && endDate ? "&end_date=" + endDate : "") +
    //             "&date_range=" +
    //             (dateRange ? "true" : "false") +
    //             "&search_query=" +
    //             data.search_query +
    //             "&order_by=" +
    //             data.order_by +
    //             "&order_direction=" +
    //             data.order_direction +
    //             "&page=" +
    //             data.page +
    //             "&size=" +
    //             data.size
    //         );

    //       if (response.status === 200) {
    //         return response.data;
    //       } else if (response.status === 400) {
    //         return {
    //           status: false,
    //           message: "Bad Request.",
    //           error: true,
    //         };
    //       } else if (response.status === 500) {
    //         return {
    //           status: false,
    //           message: "Something went wrong.",
    //           error: true,
    //         };
    //       }
    //       return {
    //         status: false,
    //         message: "Something went wrong please try again later.",
    //       };
    //     } catch (error) {
    //       const axiosError = error as AxiosError;
    //       console.error("An error occurred:", axiosError);
    //       const errorMessage =
    //         axiosError.response && typeof axiosError.response.data === "string"
    //           ? axiosError.response.data
    //           : "Unknown error";
    //       if (axiosError && axiosError.status === 401) {
    //         navigateToLogout("/logout");
    //         return {
    //           status: false,
    //           message: "UnAuthrized",
    //         };
    //       } else {
    //         return {
    //           status: false,
    //           message: errorMessage,
    //         };
    //       }
    //     }
    const RQFixedResponse: any = {
      page: 1,
      size: 50,
      total: 12,
      items: [
        {
          id: 1,
          ID: "13456789",
          party: "Zeeshan Technologies",
          type: "Payroll",
          amount: 1000,
          lastTab: "Ready",
          fromLastTabOn: "FEB 11 2026 08:30:25 AM",
          payPeriod: "Monthly",
          payPeriodEndDate: "Feb 15 2026",
          collectionUploadedOn: "Feb 15 2026",
          createdOn: "Feb 10 2025",
        },
        {
          id: 2,
          ID: "13456789",
          party: "Ahmad Technologies",
          type: "Payroll",
          amount: 1000,
          lastTab: "Ready",
          fromLastTabOn: "FEB 11 2026 08:30:25 AM",
          payPeriod: "Monthly",
          payPeriodEndDate: "Feb 15 2026",
          collectionUploadedOn: "Feb 13 2026",
          createdOn: "Feb 10 2025",
        },
        {
          id: 3,
          ID: "13456789",
          party: "Umair Technologies",
          type: "Payroll",
          amount: 5684,
          lastTab: "Ready",
          fromLastTabOn: "FEB 11 2026 08:30:25 AM",
          payPeriod: "Monthly",
          payPeriodEndDate: "Feb 15 2026",
          collectionUploadedOn: "Feb 14 2026",
          createdOn: "Feb 10 2025",
        },
        {
          id: 4,
          ID: "13456789",
          party: "Qasim Technologies",
          type: "Payroll",
          amount: 12635,
          lastTab: "Ready",
          fromLastTabOn: "FEB 11 2026 08:30:25 AM",
          payPeriod: "Monthly",
          payPeriodEndDate: "Feb 15 2026",
          collectionUploadedOn: "Feb 11 2026",
          createdOn: "Feb 10 2025",
        },
      ],
      keyField: [
        {
          field: "id",
          templateUrl: "/rq-remittance-report/details/id",
        },
      ],
      headerColumns: [
        {
          field: "ID",
          headerName: "ID",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 0,
          frozenColumn: false,
          footer: null,
          minWidth: 50,
          maxWidth: null,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "party",
          headerName: "Party",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 1,
          frozenColumn: false,
          footer: null,
          minWidth: 120,
          maxWidth: null,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "type",
          headerName: "Type",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 2,
          frozenColumn: false,
          footer: null,
          minWidth: null,
          maxWidth: null,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "amount",
          headerName: "Amount",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 3,
          frozenColumn: false,
          footer: null,
          minWidth: 120,
          maxWidth: null,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "lastTab",
          headerName: "Last Tab",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 4,
          frozenColumn: false,
          footer: null,
          minWidth: 120,
          maxWidth: null,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "fromLastTabOn",
          headerName: "From Last Tab on",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 5,
          frozenColumn: false,
          footer: null,
          minWidth: 120,
          maxWidth: null,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "payPeriod",
          headerName: "Pay Period",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 6,
          frozenColumn: false,
          footer: null,
          minWidth: 120,
          maxWidth: null,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "payPeriodEndDate",
          headerName: "Pay Period End Date",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 7,
          frozenColumn: false,
          footer: null,
          minWidth: 120,
          maxWidth: null,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "collectionUploadedOn",
          headerName: "Collection Uploaded On",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 11,
          frozenColumn: false,
          footer: null,
          minWidth: 200,
          maxWidth: null,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "createdOn",
          headerName: "Created On",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 12,
          frozenColumn: false,
          footer: null,
          minWidth: 120,
          maxWidth: null,
          fieldName: null,
          edr: "edr",
        },
      ],
    };
    return new Promise((resolve) =>
      setTimeout(() => resolve(RQFixedResponse), 1000),
    );
  },
  deleteCompletedCollectionTransactions: async (
    requestPayload: any,
  ): Promise<any | IServerError | IResponse | any> => {
    try {
      const response: AxiosResponse<any> = await http.delete<any>(
        `/v1/delete/`,
        // requestPayload
      );

      if (response.status === 201) {
        return response;
      } else if (response.status === 400) {
        return response.data;
      } else if (response.status === 401) {
        // window.location.href = '/logout';
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      if (axiosError && axiosError.status === 401) {
        requestPayload.navigateToLogout("/logout");
        return {
          status: false,
          message: "UnAuthrized",
        };
      } else if (axiosError && axiosError.status === 422) {
        return {
          status: false,
          message: `Errors : \n${format422Errors(axiosError?.response?.data)}`,
        };
      } else {
        return {
          status: false,
          message: errorMessage,
        };
      }
    }
  },

  // #################################################### Completed Payment ######################################################
  getCompletedPaymentTransacitonsList: async (
    runGetListAPICall: boolean,
    client: string | null,
    clientType: string | null,
    type: string | null,
    dateAppliesOn: string | null,
    startDate: string | null,
    endDate: string | null,
    data: IGetListRequest,
    navigateToLogout: (path: string) => void,
  ): Promise<IServerError | IResponse | null | any> => {
    // if (!runGetListAPICall) return null;

    //     try {
    //       const response: AxiosResponse<any> =
    //         await http.get<any>(
    //           "/v1/salary-profile-report" +
    //             "&employee_status=" +
    //             employeeStatus +
    //             "&employee_id=" +
    //             employeeId +
    //             (dateRange && startDate ? "&start_date=" + startDate : "") +
    //             (dateRange && endDate ? "&end_date=" + endDate : "") +
    //             "&date_range=" +
    //             (dateRange ? "true" : "false") +
    //             "&search_query=" +
    //             data.search_query +
    //             "&order_by=" +
    //             data.order_by +
    //             "&order_direction=" +
    //             data.order_direction +
    //             "&page=" +
    //             data.page +
    //             "&size=" +
    //             data.size
    //         );

    //       if (response.status === 200) {
    //         return response.data;
    //       } else if (response.status === 400) {
    //         return {
    //           status: false,
    //           message: "Bad Request.",
    //           error: true,
    //         };
    //       } else if (response.status === 500) {
    //         return {
    //           status: false,
    //           message: "Something went wrong.",
    //           error: true,
    //         };
    //       }
    //       return {
    //         status: false,
    //         message: "Something went wrong please try again later.",
    //       };
    //     } catch (error) {
    //       const axiosError = error as AxiosError;
    //       console.error("An error occurred:", axiosError);
    //       const errorMessage =
    //         axiosError.response && typeof axiosError.response.data === "string"
    //           ? axiosError.response.data
    //           : "Unknown error";
    //       if (axiosError && axiosError.status === 401) {
    //         navigateToLogout("/logout");
    //         return {
    //           status: false,
    //           message: "UnAuthrized",
    //         };
    //       } else {
    //         return {
    //           status: false,
    //           message: errorMessage,
    //         };
    //       }
    //     }
    const RQFixedResponse: any = {
      page: 1,
      size: 50,
      total: 12,
      items: [
        {
          id: 1,
          ID: "13456789",
          party: "Zeeshan Technologies",
          type: "Payroll",
          amount: 1000,
          lastTab: "Ready",
          fromLastTabOn: "FEB 11 2026 08:30:25 AM",
          payPeriod: "Monthly",
          payPeriodEndDate: "Feb 15 2026",
          UploadedOn: "Feb 15 2026",
          createdOn: "Feb 10 2025",
        },
        {
          id: 2,
          ID: "13456789",
          party: "Ahmad Technologies",
          type: "Payroll",
          amount: 1000,
          lastTab: "Ready",
          fromLastTabOn: "FEB 11 2026 08:30:25 AM",
          payPeriod: "Monthly",
          payPeriodEndDate: "Feb 15 2026",
          UploadedOn: "Feb 13 2026",
          createdOn: "Feb 10 2025",
        },
        {
          id: 3,
          ID: "13456789",
          party: "Umair Technologies",
          type: "Payroll",
          amount: 5684,
          lastTab: "Ready",
          fromLastTabOn: "FEB 11 2026 08:30:25 AM",
          payPeriod: "Monthly",
          payPeriodEndDate: "Feb 15 2026",
          UploadedOn: "Feb 14 2026",
          createdOn: "Feb 10 2025",
        },
        {
          id: 4,
          ID: "13456789",
          party: "Qasim Technologies",
          type: "Payroll",
          amount: 12635,
          lastTab: "Ready",
          fromLastTabOn: "FEB 11 2026 08:30:25 AM",
          payPeriod: "Monthly",
          payPeriodEndDate: "Feb 15 2026",
          UploadedOn: "Feb 11 2026",
          createdOn: "Feb 10 2025",
        },
      ],
      keyField: [
        {
          field: "id",
          templateUrl: "/rq-remittance-report/details/id",
        },
      ],
      headerColumns: [
        {
          field: "ID",
          headerName: "ID",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 0,
          frozenColumn: false,
          footer: null,
          minWidth: 50,
          maxWidth: null,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "party",
          headerName: "Party",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 1,
          frozenColumn: false,
          footer: null,
          minWidth: 120,
          maxWidth: null,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "type",
          headerName: "Type",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 2,
          frozenColumn: false,
          footer: null,
          minWidth: null,
          maxWidth: null,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "amount",
          headerName: "Amount",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 3,
          frozenColumn: false,
          footer: null,
          minWidth: 120,
          maxWidth: null,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "lastTab",
          headerName: "Last Tab",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 4,
          frozenColumn: false,
          footer: null,
          minWidth: 120,
          maxWidth: null,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "fromLastTabOn",
          headerName: "From Last Tab on",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 5,
          frozenColumn: false,
          footer: null,
          minWidth: 120,
          maxWidth: null,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "payPeriod",
          headerName: "Pay Period",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 6,
          frozenColumn: false,
          footer: null,
          minWidth: 120,
          maxWidth: null,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "payPeriodEndDate",
          headerName: "Pay Period End Date",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 7,
          frozenColumn: false,
          footer: null,
          minWidth: 120,
          maxWidth: null,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "UploadedOn",
          headerName: "Uploaded On",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 11,
          frozenColumn: false,
          footer: null,
          minWidth: 200,
          maxWidth: null,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "createdOn",
          headerName: "Created On",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 12,
          frozenColumn: false,
          footer: null,
          minWidth: 120,
          maxWidth: null,
          fieldName: null,
          edr: "edr",
        },
      ],
    };
    return new Promise((resolve) =>
      setTimeout(() => resolve(RQFixedResponse), 1000),
    );
  },
  deleteCompletedPaymentTransactions: async (
    requestPayload: any,
  ): Promise<any | IServerError | IResponse | any> => {
    try {
      const response: AxiosResponse<any> = await http.delete<any>(
        `/v1/delete/`,
        // requestPayload
      );

      if (response.status === 201) {
        return response;
      } else if (response.status === 400) {
        return response.data;
      } else if (response.status === 401) {
        // window.location.href = '/logout';
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      if (axiosError && axiosError.status === 401) {
        requestPayload.navigateToLogout("/logout");
        return {
          status: false,
          message: "UnAuthrized",
        };
      } else if (axiosError && axiosError.status === 422) {
        return {
          status: false,
          message: `Errors : \n${format422Errors(axiosError?.response?.data)}`,
        };
      } else {
        return {
          status: false,
          message: errorMessage,
        };
      }
    }
  },

  getPaymentMethodDDL: async (): Promise<
    any | IServerError | IResponse | null
  > => {
    const response: AxiosResponse<any[]> = await http.get<any[]>(
      `/v1/payment-method/ddl`,
    );

    if (response.status === 200) {
      return response.data;
    }

    return {
      status: false,
      message: "Something went wrong please try again later.",
    };
  },

  // #################################################### Remittance ######################################################

  // #################################################### CRA Due ######################################################
  getCRADueRemittanceTransacitonsList: async () // runGetListAPICall: boolean,
  // client: string | null,
  // clientType: string | null,
  // frequency: string | null,
  // remittanceYear: string | null,
  // remittanceDate: string | null,
  // data: IGetListRequest,
  // navigateToLogout: (path: string) => void,
  : Promise<IServerError | IResponse | null | any> => {
    // if (!runGetListAPICall) return null;

    //     try {
    //       const response: AxiosResponse<any> =
    //         await http.get<any>(
    //           "/v1/salary-profile-report" +
    //             "&employee_status=" +
    //             employeeStatus +
    //             "&employee_id=" +
    //             employeeId +
    //             (dateRange && startDate ? "&start_date=" + startDate : "") +
    //             (dateRange && endDate ? "&end_date=" + endDate : "") +
    //             "&date_range=" +
    //             (dateRange ? "true" : "false") +
    //             "&search_query=" +
    //             data.search_query +
    //             "&order_by=" +
    //             data.order_by +
    //             "&order_direction=" +
    //             data.order_direction +
    //             "&page=" +
    //             data.page +
    //             "&size=" +
    //             data.size
    //         );

    //       if (response.status === 200) {
    //         return response.data;
    //       } else if (response.status === 400) {
    //         return {
    //           status: false,
    //           message: "Bad Request.",
    //           error: true,
    //         };
    //       } else if (response.status === 500) {
    //         return {
    //           status: false,
    //           message: "Something went wrong.",
    //           error: true,
    //         };
    //       }
    //       return {
    //         status: false,
    //         message: "Something went wrong please try again later.",
    //       };
    //     } catch (error) {
    //       const axiosError = error as AxiosError;
    //       console.error("An error occurred:", axiosError);
    //       const errorMessage =
    //         axiosError.response && typeof axiosError.response.data === "string"
    //           ? axiosError.response.data
    //           : "Unknown error";
    //       if (axiosError && axiosError.status === 401) {
    //         navigateToLogout("/logout");
    //         return {
    //           status: false,
    //           message: "UnAuthrized",
    //         };
    //       } else {
    //         return {
    //           status: false,
    //           message: errorMessage,
    //         };
    //       }
    //     }
    const RQFixedResponse: any = {
      page: 1,
      size: 50,
      total: 12,
      items: [
        {
          id: 1,
          party: "Zeeshan Technologies",
          businessNo: "00000111PR0000",
          craAmount: 425.32,
          gross: "9872.35",
          empInLastPeriod: "10",
          confirmNo: null,
          lastTab: "In Collection",
          noOfPeriods: "4",
          remitFrequency: "Regular (Monthly)",
          endDate: "Feb 20 2026",
          dueDate: "Feb 21 2026",
          uploadDate: null,
          remitDate: null,
          details: [
            {
              ID: 1,
              payPeriodType: "Weekly",
              endDate: "Augest 20 2026",
              paymentDate: "Feb 20 2026",
              amount: 1524.65,
            },
            {
              ID: 2,
              payPeriodType: "Weekly",
              endDate: "Augest 20 2026",
              paymentDate: "Feb 20 2026",
              amount: 1254.65,
            },
            {
              ID: 3,
              payPeriodType: "Weekly",
              endDate: "Augest 20 2026",
              paymentDate: "Feb 20 2026",
              amount: 789.65,
            },
          ],
        },
        {
          id: 2,
          party: "Radhy",
          businessNo: "00000111PR0000",
          craAmount: 888.32,
          gross: "78941.35",
          empInLastPeriod: "10",
          confirmNo: null,
          lastTab: "In Collection",
          noOfPeriods: "4",
          remitFrequency: "Regular (Monthly)",
          endDate: "Feb 20 2026",
          dueDate: "Feb 21 2026",
          uploadDate: null,
          remitDate: null,
          details: [
            {
              ID: 1,
              payPeriodType: "Monthly",
              endDate: "Augest 20 2026",
              paymentDate: "Feb 20 2026",
              amount: 1524.65,
            },
          ],
        },
      ],
      keyField: [
        {
          field: "id",
          templateUrl: "/rq-remittance-report/details/id",
        },
      ],
      headerColumns: [
        {
          field: "party",
          headerName: "Party",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 1,
          frozenColumn: false,
          footer: null,
          minWidth: 200,
          maxWidth: 200,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "businessNo",
          headerName: "Business No.",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 2,
          frozenColumn: false,
          footer: null,
          minWidth: 120,
          maxWidth: 120,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "craAmount",
          headerName: "CRA Amount",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 3,
          frozenColumn: false,
          footer: null,
          minWidth: 120,
          maxWidth: 120,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "gross",
          headerName: "Gross",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 4,
          frozenColumn: false,
          footer: null,
          minWidth: 120,
          maxWidth: 120,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "empInLastPeriod",
          headerName: "Emp in last period",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 5,
          frozenColumn: false,
          footer: null,
          minWidth: 150,
          maxWidth: 150,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "confirmNo",
          headerName: "Confirm. No.",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 6,
          frozenColumn: false,
          footer: null,
          minWidth: 120,
          maxWidth: 120,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "lastTab",
          headerName: "Last Tab",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 7,
          frozenColumn: false,
          footer: null,
          minWidth: 180,
          maxWidth: 180,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "noOfPeriods",
          headerName: "No. of periods",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 8,
          frozenColumn: false,
          footer: null,
          minWidth: 200,
          maxWidth: 200,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "remitFrequency",
          headerName: "Remit Frequency",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 9,
          frozenColumn: false,
          footer: null,
          minWidth: 200,
          maxWidth: 120,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "endDate",
          headerName: "End Date",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 10,
          frozenColumn: false,
          footer: null,
          minWidth: 200,
          maxWidth: 120,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "dueDate",
          headerName: "Due Date",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 11,
          frozenColumn: false,
          footer: null,
          minWidth: 200,
          maxWidth: 200,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "uploadDate",
          headerName: "Upload Date",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 12,
          frozenColumn: false,
          footer: null,
          minWidth: 120,
          maxWidth: 120,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "remitDate",
          headerName: "Remit Date",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 12,
          frozenColumn: false,
          footer: null,
          minWidth: 120,
          maxWidth: 120,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "details",
          headerName: "Details",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "center",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 12,
          frozenColumn: false,
          footer: null,
          minWidth: 120,
          maxWidth: 120,
          fieldName: null,
          edr: "edr",
        },
      ],
    };
    return new Promise((resolve) =>
      setTimeout(() => resolve(RQFixedResponse), 1000),
    );
  },
  saveCRADueRemittanceTransactions: async (
    requestPayload: any,
  ): Promise<IServerError | IResponse | any> => {
    try {
      const response: AxiosResponse<any> = await http.post<any>(
        `/v1/save/`,
        // requestPayload.data
      );

      if (response.status === 201) {
        return response;
      } else if (response.status === 400) {
        return response.data;
      } else if (response.status === 401) {
        // window.location.href = '/logout';
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      if (axiosError && axiosError.status === 401) {
        requestPayload.navigateToLogout("/logout");
        return {
          status: false,
          message: "UnAuthrized",
        };
      } else if (axiosError && axiosError.status === 422) {
        return {
          status: false,
          message: `Errors : \n${format422Errors(axiosError?.response?.data)}`,
        };
      } else {
        return {
          status: false,
          message: errorMessage,
        };
      }
    }
  },
  deleteCRADueRemittanceTransactions: async (
    requestPayload: any,
  ): Promise<any | IServerError | IResponse | any> => {
    try {
      const response: AxiosResponse<any> = await http.delete<any>(
        `/v1/delete/`,
        // requestPayload
      );

      if (response.status === 201) {
        return response;
      } else if (response.status === 400) {
        return response.data;
      } else if (response.status === 401) {
        // window.location.href = '/logout';
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      if (axiosError && axiosError.status === 401) {
        requestPayload.navigateToLogout("/logout");
        return {
          status: false,
          message: "UnAuthrized",
        };
      } else if (axiosError && axiosError.status === 422) {
        return {
          status: false,
          message: `Errors : \n${format422Errors(axiosError?.response?.data)}`,
        };
      } else {
        return {
          status: false,
          message: errorMessage,
        };
      }
    }
  },
  processedOnCRAWebsiteCRADueRemittanceTransactions: async (
    requestPayload: any,
  ): Promise<any | IServerError | IResponse | any> => {
    try {
      const response: AxiosResponse<any> = await http.post<any>(
        `/v1/upload`,
        // requestPayload
      );

      if (response.status === 201) {
        return response;
      } else if (response.status === 400) {
        return response.data;
      } else if (response.status === 401) {
        // window.location.href = '/logout';
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      return {
        status: false,
        message: errorMessage,
      };
    }
  },

  // #################################################### RQ Due ######################################################
  getRQDueRemittanceTransacitonsList: async () // runGetListAPICall: boolean,
  // client: string | null,
  // clientType: string | null,
  // frequency: string | null,
  // remittanceYear: string | null,
  // remittanceDate: string | null,
  // data: IGetListRequest,
  // navigateToLogout: (path: string) => void,
  : Promise<IServerError | IResponse | null | any> => {
    // if (!runGetListAPICall) return null;

    //     try {
    //       const response: AxiosResponse<any> =
    //         await http.get<any>(
    //           "/v1/salary-profile-report" +
    //             "&employee_status=" +
    //             employeeStatus +
    //             "&employee_id=" +
    //             employeeId +
    //             (dateRange && startDate ? "&start_date=" + startDate : "") +
    //             (dateRange && endDate ? "&end_date=" + endDate : "") +
    //             "&date_range=" +
    //             (dateRange ? "true" : "false") +
    //             "&search_query=" +
    //             data.search_query +
    //             "&order_by=" +
    //             data.order_by +
    //             "&order_direction=" +
    //             data.order_direction +
    //             "&page=" +
    //             data.page +
    //             "&size=" +
    //             data.size
    //         );

    //       if (response.status === 200) {
    //         return response.data;
    //       } else if (response.status === 400) {
    //         return {
    //           status: false,
    //           message: "Bad Request.",
    //           error: true,
    //         };
    //       } else if (response.status === 500) {
    //         return {
    //           status: false,
    //           message: "Something went wrong.",
    //           error: true,
    //         };
    //       }
    //       return {
    //         status: false,
    //         message: "Something went wrong please try again later.",
    //       };
    //     } catch (error) {
    //       const axiosError = error as AxiosError;
    //       console.error("An error occurred:", axiosError);
    //       const errorMessage =
    //         axiosError.response && typeof axiosError.response.data === "string"
    //           ? axiosError.response.data
    //           : "Unknown error";
    //       if (axiosError && axiosError.status === 401) {
    //         navigateToLogout("/logout");
    //         return {
    //           status: false,
    //           message: "UnAuthrized",
    //         };
    //       } else {
    //         return {
    //           status: false,
    //           message: errorMessage,
    //         };
    //       }
    //     }
    const RQFixedResponse: any = {
      page: 1,
      size: 50,
      total: 12,
      items: [
        {
          id: 1,
          party: "Zeeshan Technologies",
          businessNo: "00000111PR0000",

          // add it in rq due
          rqAmount: 425.32,
          rqBreakup: {
            incomeTax: 545.2,
            qpp: 789.21,
            hsf: 444.2,
            qpip: 102.3,
            csst: 555.5,
            total: 1545.2,
          },

          gross: "9872.35",
          empInLastPeriod: "10",
          confirmNo: null,
          lastTab: "In Collection",
          noOfPeriods: "4",
          remitFrequency: "Regular (Monthly)",
          endDate: "Feb 20 2026",
          dueDate: "Feb 21 2026",
          uploadDate: null,
          remitDate: null,
          details: [
            {
              ID: 1,
              payPeriodType: "Weekly",
              endDate: "Augest 20 2026",
              paymentDate: "Feb 20 2026",
              amount: 1524.65,
            },
            {
              ID: 2,
              payPeriodType: "Weekly",
              endDate: "Augest 20 2026",
              paymentDate: "Feb 20 2026",
              amount: 1254.65,
            },
            {
              ID: 3,
              payPeriodType: "Weekly",
              endDate: "Augest 20 2026",
              paymentDate: "Feb 20 2026",
              amount: 789.65,
            },
          ],
        },
        {
          id: 2,
          party: "Radhy",
          businessNo: "00000111PR0000",

          // add it in rq due
          rqAmount: 888.32,
          rqBreakup: {
            incomeTax: 12.2,
            qpp: 34.21,
            hsf: 56.2,
            qpip: 78.3,
            csst: 90.5,
            total: 609.2,
          },

          gross: "78941.35",
          empInLastPeriod: "10",
          confirmNo: null,
          lastTab: "In Collection",
          noOfPeriods: "4",
          remitFrequency: "Regular (Monthly)",
          endDate: "Feb 20 2026",
          dueDate: "Feb 21 2026",
          uploadDate: null,
          remitDate: null,
          details: [
            {
              ID: 1,
              payPeriodType: "Monthly",
              endDate: "Augest 20 2026",
              paymentDate: "Feb 20 2026",
              amount: 1524.65,
            },
          ],
        },
      ],
      keyField: [
        {
          field: "id",
          templateUrl: "/rq-remittance-report/details/id",
        },
      ],
      headerColumns: [
        {
          field: "party",
          headerName: "Party",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 1,
          frozenColumn: false,
          footer: null,
          minWidth: 200,
          maxWidth: 200,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "businessNo",
          headerName: "Business No.",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 2,
          frozenColumn: false,
          footer: null,
          minWidth: 120,
          maxWidth: 120,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "rqAmount",
          headerName: "RQ Amount",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 3,
          frozenColumn: false,
          footer: null,
          minWidth: 120,
          maxWidth: 120,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "gross",
          headerName: "Gross",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 4,
          frozenColumn: false,
          footer: null,
          minWidth: 120,
          maxWidth: 120,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "empInLastPeriod",
          headerName: "Emp in last period",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 5,
          frozenColumn: false,
          footer: null,
          minWidth: 150,
          maxWidth: 150,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "confirmNo",
          headerName: "Confirm. No.",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 6,
          frozenColumn: false,
          footer: null,
          minWidth: 120,
          maxWidth: 120,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "lastTab",
          headerName: "Last Tab",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 7,
          frozenColumn: false,
          footer: null,
          minWidth: 180,
          maxWidth: 180,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "noOfPeriods",
          headerName: "No. of periods",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 8,
          frozenColumn: false,
          footer: null,
          minWidth: 200,
          maxWidth: 200,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "remitFrequency",
          headerName: "Remit Frequency",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 9,
          frozenColumn: false,
          footer: null,
          minWidth: 200,
          maxWidth: 120,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "endDate",
          headerName: "End Date",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 10,
          frozenColumn: false,
          footer: null,
          minWidth: 200,
          maxWidth: 120,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "dueDate",
          headerName: "Due Date",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 11,
          frozenColumn: false,
          footer: null,
          minWidth: 200,
          maxWidth: 200,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "uploadDate",
          headerName: "Upload Date",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 12,
          frozenColumn: false,
          footer: null,
          minWidth: 120,
          maxWidth: 120,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "remitDate",
          headerName: "Remit Date",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 12,
          frozenColumn: false,
          footer: null,
          minWidth: 120,
          maxWidth: 120,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "details",
          headerName: "Details",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "center",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 12,
          frozenColumn: false,
          footer: null,
          minWidth: 120,
          maxWidth: 120,
          fieldName: null,
          edr: "edr",
        },
      ],
    };
    return new Promise((resolve) =>
      setTimeout(() => resolve(RQFixedResponse), 1000),
    );
  },
  saveRQDueRemittanceTransactions: async (
    requestPayload: any,
  ): Promise<IServerError | IResponse | any> => {
    try {
      const response: AxiosResponse<any> = await http.post<any>(
        `/v1/save/`,
        // requestPayload.data
      );

      if (response.status === 201) {
        return response;
      } else if (response.status === 400) {
        return response.data;
      } else if (response.status === 401) {
        // window.location.href = '/logout';
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      if (axiosError && axiosError.status === 401) {
        requestPayload.navigateToLogout("/logout");
        return {
          status: false,
          message: "UnAuthrized",
        };
      } else if (axiosError && axiosError.status === 422) {
        return {
          status: false,
          message: `Errors : \n${format422Errors(axiosError?.response?.data)}`,
        };
      } else {
        return {
          status: false,
          message: errorMessage,
        };
      }
    }
  },
  deleteRQDueRemittanceTransactions: async (
    requestPayload: any,
  ): Promise<any | IServerError | IResponse | any> => {
    try {
      const response: AxiosResponse<any> = await http.delete<any>(
        `/v1/delete/`,
        // requestPayload
      );

      if (response.status === 201) {
        return response;
      } else if (response.status === 400) {
        return response.data;
      } else if (response.status === 401) {
        // window.location.href = '/logout';
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      if (axiosError && axiosError.status === 401) {
        requestPayload.navigateToLogout("/logout");
        return {
          status: false,
          message: "UnAuthrized",
        };
      } else if (axiosError && axiosError.status === 422) {
        return {
          status: false,
          message: `Errors : \n${format422Errors(axiosError?.response?.data)}`,
        };
      } else {
        return {
          status: false,
          message: errorMessage,
        };
      }
    }
  },
  processedOnRQWebsiteRQDueRemittanceTransactions: async (
    requestPayload: any,
  ): Promise<any | IServerError | IResponse | any> => {
    try {
      const response: AxiosResponse<any> = await http.post<any>(
        `/v1/upload`,
        // requestPayload
      );

      if (response.status === 201) {
        return response;
      } else if (response.status === 400) {
        return response.data;
      } else if (response.status === 401) {
        // window.location.href = '/logout';
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      return {
        status: false,
        message: errorMessage,
      };
    }
  },

  // #################################################### EHTOntario Due ######################################################
  getEHTOntarioDueRemittanceTransacitonsList: async (
    runGetListAPICall: boolean,
    client: string | null,
    clientType: string | null,
    frequency: string | null,
    remittanceYear: string | null,
    remittanceDate: string | null,
    data: IGetListRequest,
    navigateToLogout: (path: string) => void,
  ): Promise<IServerError | IResponse | null | any> => {
    // if (!runGetListAPICall) return null;

    //     try {
    //       const response: AxiosResponse<any> =
    //         await http.get<any>(
    //           "/v1/salary-profile-report" +
    //             "&employee_status=" +
    //             employeeStatus +
    //             "&employee_id=" +
    //             employeeId +
    //             (dateRange && startDate ? "&start_date=" + startDate : "") +
    //             (dateRange && endDate ? "&end_date=" + endDate : "") +
    //             "&date_range=" +
    //             (dateRange ? "true" : "false") +
    //             "&search_query=" +
    //             data.search_query +
    //             "&order_by=" +
    //             data.order_by +
    //             "&order_direction=" +
    //             data.order_direction +
    //             "&page=" +
    //             data.page +
    //             "&size=" +
    //             data.size
    //         );

    //       if (response.status === 200) {
    //         return response.data;
    //       } else if (response.status === 400) {
    //         return {
    //           status: false,
    //           message: "Bad Request.",
    //           error: true,
    //         };
    //       } else if (response.status === 500) {
    //         return {
    //           status: false,
    //           message: "Something went wrong.",
    //           error: true,
    //         };
    //       }
    //       return {
    //         status: false,
    //         message: "Something went wrong please try again later.",
    //       };
    //     } catch (error) {
    //       const axiosError = error as AxiosError;
    //       console.error("An error occurred:", axiosError);
    //       const errorMessage =
    //         axiosError.response && typeof axiosError.response.data === "string"
    //           ? axiosError.response.data
    //           : "Unknown error";
    //       if (axiosError && axiosError.status === 401) {
    //         navigateToLogout("/logout");
    //         return {
    //           status: false,
    //           message: "UnAuthrized",
    //         };
    //       } else {
    //         return {
    //           status: false,
    //           message: errorMessage,
    //         };
    //       }
    //     }
    const RQFixedResponse: any = {
      page: 1,
      size: 50,
      total: 12,
      items: [
        {
          id: 1,
          party: "Zeeshan Technologies",
          businessNo: "00000111PR0000",
          ehtOntarioAmount: 425.32,
          gross: "9872.35",
          empInLastPeriod: "10",
          confirmNo: null,
          lastTab: "In Collection",
          noOfPeriods: "4",
          remitFrequency: "Regular (Monthly)",
          endDate: "Feb 20 2026",
          dueDate: "Feb 21 2026",
          uploadDate: null,
          remitDate: null,
          details: [
            {
              ID: 1,
              payPeriodType: "Weekly",
              endDate: "Augest 20 2026",
              paymentDate: "Feb 20 2026",
              amount: 1524.65,
            },
            {
              ID: 2,
              payPeriodType: "Weekly",
              endDate: "Augest 20 2026",
              paymentDate: "Feb 20 2026",
              amount: 1254.65,
            },
            {
              ID: 3,
              payPeriodType: "Weekly",
              endDate: "Augest 20 2026",
              paymentDate: "Feb 20 2026",
              amount: 789.65,
            },
          ],
        },
        {
          id: 2,
          party: "Radhy",
          businessNo: "00000111PR0000",
          ehtOntarioAmount: 888.32,
          gross: "78941.35",
          empInLastPeriod: "10",
          confirmNo: null,
          lastTab: "In Collection",
          noOfPeriods: "4",
          remitFrequency: "Regular (Monthly)",
          endDate: "Feb 20 2026",
          dueDate: "Feb 21 2026",
          uploadDate: null,
          remitDate: null,
          details: [
            {
              ID: 1,
              payPeriodType: "Monthly",
              endDate: "Augest 20 2026",
              paymentDate: "Feb 20 2026",
              amount: 1524.65,
            },
          ],
        },
      ],
      keyField: [
        {
          field: "id",
          templateUrl: "/rq-remittance-report/details/id",
        },
      ],
      headerColumns: [
        {
          field: "party",
          headerName: "Party",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 1,
          frozenColumn: false,
          footer: null,
          minWidth: 200,
          maxWidth: 200,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "businessNo",
          headerName: "Business No.",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 2,
          frozenColumn: false,
          footer: null,
          minWidth: 120,
          maxWidth: 120,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "ehtOntarioAmount",
          headerName: "EHT Ontario Amount",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 3,
          frozenColumn: false,
          footer: null,
          minWidth: 120,
          maxWidth: 120,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "gross",
          headerName: "Gross",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 4,
          frozenColumn: false,
          footer: null,
          minWidth: 120,
          maxWidth: 120,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "empInLastPeriod",
          headerName: "Emp in last period",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 5,
          frozenColumn: false,
          footer: null,
          minWidth: 150,
          maxWidth: 150,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "confirmNo",
          headerName: "Confirm. No.",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 6,
          frozenColumn: false,
          footer: null,
          minWidth: 120,
          maxWidth: 120,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "lastTab",
          headerName: "Last Tab",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 7,
          frozenColumn: false,
          footer: null,
          minWidth: 180,
          maxWidth: 180,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "noOfPeriods",
          headerName: "No. of periods",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 8,
          frozenColumn: false,
          footer: null,
          minWidth: 200,
          maxWidth: 200,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "remitFrequency",
          headerName: "Remit Frequency",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 9,
          frozenColumn: false,
          footer: null,
          minWidth: 200,
          maxWidth: 120,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "endDate",
          headerName: "End Date",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 10,
          frozenColumn: false,
          footer: null,
          minWidth: 200,
          maxWidth: 120,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "dueDate",
          headerName: "Due Date",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 11,
          frozenColumn: false,
          footer: null,
          minWidth: 200,
          maxWidth: 200,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "uploadDate",
          headerName: "Upload Date",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 12,
          frozenColumn: false,
          footer: null,
          minWidth: 120,
          maxWidth: 120,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "remitDate",
          headerName: "Remit Date",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 12,
          frozenColumn: false,
          footer: null,
          minWidth: 120,
          maxWidth: 120,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "details",
          headerName: "Details",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "center",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 12,
          frozenColumn: false,
          footer: null,
          minWidth: 120,
          maxWidth: 120,
          fieldName: null,
          edr: "edr",
        },
      ],
    };
    return new Promise((resolve) =>
      setTimeout(() => resolve(RQFixedResponse), 1000),
    );
  },
  saveEHTOntarioDueRemittanceTransactions: async (
    requestPayload: any,
  ): Promise<IServerError | IResponse | any> => {
    try {
      const response: AxiosResponse<any> = await http.post<any>(
        `/v1/save/`,
        // requestPayload.data
      );

      if (response.status === 201) {
        return response;
      } else if (response.status === 400) {
        return response.data;
      } else if (response.status === 401) {
        // window.location.href = '/logout';
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      if (axiosError && axiosError.status === 401) {
        requestPayload.navigateToLogout("/logout");
        return {
          status: false,
          message: "UnAuthrized",
        };
      } else if (axiosError && axiosError.status === 422) {
        return {
          status: false,
          message: `Errors : \n${format422Errors(axiosError?.response?.data)}`,
        };
      } else {
        return {
          status: false,
          message: errorMessage,
        };
      }
    }
  },
  deleteEHTOntarioDueRemittanceTransactions: async (
    requestPayload: any,
  ): Promise<any | IServerError | IResponse | any> => {
    try {
      const response: AxiosResponse<any> = await http.delete<any>(
        `/v1/delete/`,
        // requestPayload
      );

      if (response.status === 201) {
        return response;
      } else if (response.status === 400) {
        return response.data;
      } else if (response.status === 401) {
        // window.location.href = '/logout';
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      if (axiosError && axiosError.status === 401) {
        requestPayload.navigateToLogout("/logout");
        return {
          status: false,
          message: "UnAuthrized",
        };
      } else if (axiosError && axiosError.status === 422) {
        return {
          status: false,
          message: `Errors : \n${format422Errors(axiosError?.response?.data)}`,
        };
      } else {
        return {
          status: false,
          message: errorMessage,
        };
      }
    }
  },
  processedOnEHTOntarioWebsiteEHTOntarioDueRemittanceTransactions: async (
    requestPayload: any,
  ): Promise<any | IServerError | IResponse | any> => {
    try {
      const response: AxiosResponse<any> = await http.post<any>(
        `/v1/upload`,
        // requestPayload
      );

      if (response.status === 201) {
        return response;
      } else if (response.status === 400) {
        return response.data;
      } else if (response.status === 401) {
        // window.location.href = '/logout';
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      return {
        status: false,
        message: errorMessage,
      };
    }
  },

  // #################################################### Remitted ######################################################
  getRemittedRemittanceTransacitonsList: async (
    runGetListAPICall: boolean,
    screenType: number,
    accountant: string,
    client: string,
    clientType: string,
    frequency: string | null,
    remittanceYear: string | null,
    remittanceMonth: string | null,
    data: IGetListRequest,
    navigateToLogout: (path: string) => void,
  ): Promise<IServerError | IResponse | null | any> => {
    // if (!runGetListAPICall) return null;

    //     try {
    //       const response: AxiosResponse<any> =
    //         await http.get<any>(
    //           "/v1/salary-profile-report" +
    //             "&employee_status=" +
    //             employeeStatus +
    //             "&employee_id=" +
    //             employeeId +
    //             (dateRange && startDate ? "&start_date=" + startDate : "") +
    //             (dateRange && endDate ? "&end_date=" + endDate : "") +
    //             "&date_range=" +
    //             (dateRange ? "true" : "false") +
    //             "&search_query=" +
    //             data.search_query +
    //             "&order_by=" +
    //             data.order_by +
    //             "&order_direction=" +
    //             data.order_direction +
    //             "&page=" +
    //             data.page +
    //             "&size=" +
    //             data.size
    //         );

    //       if (response.status === 200) {
    //         return response.data;
    //       } else if (response.status === 400) {
    //         return {
    //           status: false,
    //           message: "Bad Request.",
    //           error: true,
    //         };
    //       } else if (response.status === 500) {
    //         return {
    //           status: false,
    //           message: "Something went wrong.",
    //           error: true,
    //         };
    //       }
    //       return {
    //         status: false,
    //         message: "Something went wrong please try again later.",
    //       };
    //     } catch (error) {
    //       const axiosError = error as AxiosError;
    //       console.error("An error occurred:", axiosError);
    //       const errorMessage =
    //         axiosError.response && typeof axiosError.response.data === "string"
    //           ? axiosError.response.data
    //           : "Unknown error";
    //       if (axiosError && axiosError.status === 401) {
    //         navigateToLogout("/logout");
    //         return {
    //           status: false,
    //           message: "UnAuthrized",
    //         };
    //       } else {
    //         return {
    //           status: false,
    //           message: errorMessage,
    //         };
    //       }
    //     }
    const RQFixedResponse: any = {
      page: 1,
      size: 50,
      total: 12,
      items: [
        {
          id: 1,
          party: "Zeeshan Technologies",
          businessNo: "00000111PR0000",
          lastTab: "CRA Due",
          noOfPeriods: "4",
          type: "CRA",
          remitFrequency: "Regular (Monthly)",
          endDate: "Feb 20 2026",
          dueDate: "Feb 21 2026",
          amount: 425.32,
          remitDate: null,
          confirmNo: null,
          deductionDate: null,
          notes: null,
          details: [
            {
              ID: 1,
              payPeriodType: "Weekly",
              endDate: "Augest 20 2026",
              paymentDate: "Feb 20 2026",
              amount: 1524.65,
            },
            {
              ID: 2,
              payPeriodType: "Weekly",
              endDate: "Augest 20 2026",
              paymentDate: "Feb 20 2026",
              amount: 1254.65,
            },
            {
              ID: 3,
              payPeriodType: "Weekly",
              endDate: "Augest 20 2026",
              paymentDate: "Feb 20 2026",
              amount: 789.65,
            },
          ],
        },
      ],
      keyField: [
        {
          field: "id",
          templateUrl: "/rq-remittance-report/details/id",
        },
      ],
      headerColumns: [
        {
          field: "party",
          headerName: "Party",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 1,
          frozenColumn: false,
          footer: null,
          minWidth: 200,
          maxWidth: 200,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "businessNo",
          headerName: "Business No.",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 2,
          frozenColumn: false,
          footer: null,
          minWidth: 120,
          maxWidth: 120,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "lastTab",
          headerName: "Last Tab",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 7,
          frozenColumn: false,
          footer: null,
          minWidth: 180,
          maxWidth: 180,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "noOfPeriods",
          headerName: "No. of periods",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 8,
          frozenColumn: false,
          footer: null,
          minWidth: 200,
          maxWidth: 200,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "type",
          headerName: "Type",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 8,
          frozenColumn: false,
          footer: null,
          minWidth: 200,
          maxWidth: 200,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "remitFrequency",
          headerName: "Remit Frequency",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 9,
          frozenColumn: false,
          footer: null,
          minWidth: 200,
          maxWidth: 120,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "endDate",
          headerName: "End Date",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 10,
          frozenColumn: false,
          footer: null,
          minWidth: 200,
          maxWidth: 120,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "dueDate",
          headerName: "Due Date",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 11,
          frozenColumn: false,
          footer: null,
          minWidth: 200,
          maxWidth: 200,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "amount",
          headerName: "Amount",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 3,
          frozenColumn: false,
          footer: null,
          minWidth: 120,
          maxWidth: 120,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "remitDate",
          headerName: "Remit Date",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 12,
          frozenColumn: false,
          footer: null,
          minWidth: 120,
          maxWidth: 120,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "confirmNo",
          headerName: "Confirm. No.",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 6,
          frozenColumn: false,
          footer: null,
          minWidth: 120,
          maxWidth: 120,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "deductionDate",
          headerName: "Deduction Date",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 12,
          frozenColumn: false,
          footer: null,
          minWidth: 120,
          maxWidth: 120,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "notes",
          headerName: "Notes",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 12,
          frozenColumn: false,
          footer: null,
          minWidth: 120,
          maxWidth: 120,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "details",
          headerName: "Details",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "center",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 12,
          frozenColumn: false,
          footer: null,
          minWidth: 120,
          maxWidth: 120,
          fieldName: null,
          edr: "edr",
        },
      ],
    };
    return new Promise((resolve) =>
      setTimeout(() => resolve(RQFixedResponse), 1000),
    );
  },
  saveRemittedRemittanceTransactions: async (
    requestPayload: any,
  ): Promise<IServerError | IResponse | any> => {
    try {
      const response: AxiosResponse<any> = await http.post<any>(
        `/v1/save/`,
        // requestPayload.data
      );

      if (response.status === 201) {
        return response;
      } else if (response.status === 400) {
        return response.data;
      } else if (response.status === 401) {
        // window.location.href = '/logout';
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      if (axiosError && axiosError.status === 401) {
        requestPayload.navigateToLogout("/logout");
        return {
          status: false,
          message: "UnAuthrized",
        };
      } else if (axiosError && axiosError.status === 422) {
        return {
          status: false,
          message: `Errors : \n${format422Errors(axiosError?.response?.data)}`,
        };
      } else {
        return {
          status: false,
          message: errorMessage,
        };
      }
    }
  },
  deleteRemittedRemittanceTransactions: async (
    requestPayload: any,
  ): Promise<any | IServerError | IResponse | any> => {
    try {
      const response: AxiosResponse<any> = await http.delete<any>(
        `/v1/delete/`,
        // requestPayload
      );

      if (response.status === 201) {
        return response;
      } else if (response.status === 400) {
        return response.data;
      } else if (response.status === 401) {
        // window.location.href = '/logout';
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      if (axiosError && axiosError.status === 401) {
        requestPayload.navigateToLogout("/logout");
        return {
          status: false,
          message: "UnAuthrized",
        };
      } else if (axiosError && axiosError.status === 422) {
        return {
          status: false,
          message: `Errors : \n${format422Errors(axiosError?.response?.data)}`,
        };
      } else {
        return {
          status: false,
          message: errorMessage,
        };
      }
    }
  },
  amountRemittedRemittanceTransactions: async (
    requestPayload: any,
  ): Promise<any | IServerError | IResponse | any> => {
    try {
      const response: AxiosResponse<any> = await http.post<any>(
        `/v1/upload`,
        // requestPayload
      );

      if (response.status === 201) {
        return response;
      } else if (response.status === 400) {
        return response.data;
      } else if (response.status === 401) {
        // window.location.href = '/logout';
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      return {
        status: false,
        message: errorMessage,
      };
    }
  },
  sendEmailRemittedRemittanceTransactions: async (
    requestPayload: any,
  ): Promise<any | IServerError | IResponse | any> => {
    try {
      const response: AxiosResponse<any> = await http.post<any>(
        `/v1/upload`,
        // requestPayload
      );

      if (response.status === 201) {
        return response;
      } else if (response.status === 400) {
        return response.data;
      } else if (response.status === 401) {
        // window.location.href = '/logout';
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      return {
        status: false,
        message: errorMessage,
      };
    }
  },
  forceRemitRemittedRemittanceTransactions: async (
    requestPayload: any,
  ): Promise<any | IServerError | IResponse | any> => {
    try {
      const response: AxiosResponse<any> = await http.post<any>(
        `/v1/upload`,
        // requestPayload
      );

      if (response.status === 201) {
        return response;
      } else if (response.status === 400) {
        return response.data;
      } else if (response.status === 401) {
        // window.location.href = '/logout';
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      return {
        status: false,
        message: errorMessage,
      };
    }
  },

  // #################################################### Error Transaction ######################################################
  getErrorTransacitonsList: async (
    runGetListAPICall: boolean,
    client: string | null,
    clientType: string | null,
    type: string | null,
    dateAppliesOn: string | null,
    startDate: string | null,
    endDate: string | null,
    data: IGetListRequest,
    navigateToLogout: (path: string) => void,
  ): Promise<IServerError | IResponse | null | any> => {
    // if (!runGetListAPICall) return null;

    //     try {
    //       const response: AxiosResponse<any> =
    //         await http.get<any>(
    //           "/v1/salary-profile-report" +
    //             "&employee_status=" +
    //             employeeStatus +
    //             "&employee_id=" +
    //             employeeId +
    //             (dateRange && startDate ? "&start_date=" + startDate : "") +
    //             (dateRange && endDate ? "&end_date=" + endDate : "") +
    //             "&date_range=" +
    //             (dateRange ? "true" : "false") +
    //             "&search_query=" +
    //             data.search_query +
    //             "&order_by=" +
    //             data.order_by +
    //             "&order_direction=" +
    //             data.order_direction +
    //             "&page=" +
    //             data.page +
    //             "&size=" +
    //             data.size
    //         );

    //       if (response.status === 200) {
    //         return response.data;
    //       } else if (response.status === 400) {
    //         return {
    //           status: false,
    //           message: "Bad Request.",
    //           error: true,
    //         };
    //       } else if (response.status === 500) {
    //         return {
    //           status: false,
    //           message: "Something went wrong.",
    //           error: true,
    //         };
    //       }
    //       return {
    //         status: false,
    //         message: "Something went wrong please try again later.",
    //       };
    //     } catch (error) {
    //       const axiosError = error as AxiosError;
    //       console.error("An error occurred:", axiosError);
    //       const errorMessage =
    //         axiosError.response && typeof axiosError.response.data === "string"
    //           ? axiosError.response.data
    //           : "Unknown error";
    //       if (axiosError && axiosError.status === 401) {
    //         navigateToLogout("/logout");
    //         return {
    //           status: false,
    //           message: "UnAuthrized",
    //         };
    //       } else {
    //         return {
    //           status: false,
    //           message: errorMessage,
    //         };
    //       }
    //     }
    const RQFixedResponse: any = {
      page: 1,
      size: 50,
      total: 12,
      items: [
        {
          id: 1,
          ID: "13456789",
          party: "Zeeshan Technologies",
          type: "Payroll",
          amount: 1000,
          lastTab: "Ready",
          uploadDateAndTime: "FEB 11 2026 08:30:25 AM",
          errorMessage: "NSF (100.00)",
          createdOn: "Feb 10 2025",
        },
        {
          id: 2,
          ID: "13456789",
          party: "Ahmad Technologies",
          type: "Payroll",
          amount: 1000,
          lastTab: "Ready",
          uploadDateAndTime: "FEB 11 2026 08:30:25 AM",
          errorMessage: "Collection Account Error",
          createdOn: "Feb 10 2025",
        },
      ],
      keyField: [
        {
          field: "id",
          templateUrl: "/rq-remittance-report/details/id",
        },
      ],
      headerColumns: [
        {
          field: "ID",
          headerName: "ID",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 0,
          frozenColumn: false,
          footer: null,
          minWidth: 50,
          maxWidth: null,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "party",
          headerName: "Party",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 1,
          frozenColumn: false,
          footer: null,
          minWidth: 120,
          maxWidth: null,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "type",
          headerName: "Type",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 2,
          frozenColumn: false,
          footer: null,
          minWidth: 120,
          maxWidth: null,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "amount",
          headerName: "Amount",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 3,
          frozenColumn: false,
          footer: null,
          minWidth: 120,
          maxWidth: null,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "lastTab",
          headerName: "Last Tab",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 4,
          frozenColumn: false,
          footer: null,
          minWidth: 120,
          maxWidth: null,
          fieldName: null,
          edr: "edr",
        },

        {
          field: "uploadDateAndTime",
          headerName: "Upload Date and Time",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 9,
          frozenColumn: false,
          footer: null,
          minWidth: 200,
          maxWidth: null,
          fieldName: null,
          edr: "edr",
        },

        {
          field: "errorMessage",
          headerName: "Error Message",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 11,
          frozenColumn: false,
          footer: null,
          minWidth: 200,
          maxWidth: null,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "createdOn",
          headerName: "Created On",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 12,
          frozenColumn: false,
          footer: null,
          minWidth: 120,
          maxWidth: null,
          fieldName: null,
          edr: "edr",
        },
      ],
    };
    return new Promise((resolve) =>
      setTimeout(() => resolve(RQFixedResponse), 1000),
    );
  },
  errorRemovedOrReprocessErrorTransactions: async (
    requestPayload: any,
  ): Promise<IServerError | IResponse | any> => {
    try {
      const response: AxiosResponse<any> = await http.post<any>(
        `/v1/ready/`,
        // requestPayload.data
      );

      if (response.status === 201) {
        return response;
      } else if (response.status === 400) {
        return response.data;
      } else if (response.status === 401) {
        // window.location.href = '/logout';
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      if (axiosError && axiosError.status === 401) {
        requestPayload.navigateToLogout("/logout");
        return {
          status: false,
          message: "UnAuthrized",
        };
      } else if (axiosError && axiosError.status === 422) {
        return {
          status: false,
          message: `Errors : \n${format422Errors(axiosError?.response?.data)}`,
        };
      } else {
        return {
          status: false,
          message: errorMessage,
        };
      }
    }
  },

  // #################################################### collection Uploaded ######################################################
  getCollectionUploadedTransacitonsList: async (
    data: IGetListRequest,
    navigateToLogout: (path: string) => void,
  ): Promise<IServerError | IResponse | null | any> => {
    try {
      const response: AxiosResponse<any> = await http.get<any>(
        "/v1/direct-deposit-file/?search_query=" +
          data.search_query +
          "&order_by=" +
          data.order_by +
          "&order_direction=" +
          data.order_direction +
          "&page=" +
          data.page +
          "&size=" +
          data.size,
      );

      if (response.status === 200) {
        return response.data;
      } else if (response.status === 400) {
        return {
          status: false,
          message: "Bad Request.",
          error: true,
        };
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }
      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      if (axiosError && axiosError.status === 401) {
        navigateToLogout("/logout");
        return {
          status: false,
          message: "UnAuthrized",
        };
      } else {
        return {
          status: false,
          message: errorMessage,
        };
      }
    }
    // const RQFixedResponse: any = {
    //   page: 1,
    //   size: 50,
    //   total: 12,
    //   items: [
    //     {
    //       id: 1,
    //       fileName: "PG-DEBIT-2022-07-19-1.txt",
    //       description: "210185.02",
    //       uploadedOn: "july 20 2022 08:16:15 AM",
    //     },
    //     {
    //       id: 2,
    //       fileName: "PG-DEBIT-2022-07-19-1.txt",
    //       description: "210185.02",
    //       uploadedOn: "july 20 2022 08:16:15 AM",
    //     },
    //     {
    //       id: 3,
    //       fileName: "PG-DEBIT-2022-07-19-1.txt",
    //       description: "210185.02",
    //       uploadedOn: "july 20 2022 08:16:15 AM",
    //     },
    //     {
    //       id: 4,
    //       fileName: "PG-DEBIT-2022-07-19-1.txt",
    //       description: "210185.02",
    //       uploadedOn: "july 20 2022 08:16:15 AM",
    //     },
    //   ],
    //   keyField: [
    //     {
    //       field: "id",
    //       templateUrl: "/rq-remittance-report/details/id",
    //     },
    //   ],
    //   headerColumns: [
    //     {
    //       field: "fileName",
    //       headerName: "File Name",
    //       description: "",
    //       flex: 1,
    //       hide: false,
    //       hideable: false,
    //       sortable: true,
    //       sortingOrder: "asc",
    //       resizable: false,
    //       type: "string",
    //       align: "left",
    //       headerAlign: "left",
    //       hideSortIcons: false,
    //       disableColumnMenu: true,
    //       disableReorder: true,
    //       disableExport: true,
    //       maskable: false,
    //       isFixed: true,
    //       isEditable: true,
    //       sequence: 0,
    //       frozenColumn: false,
    //       footer: null,
    //       minWidth: 50,
    //       maxWidth: null,
    //       fieldName: null,
    //       edr: "edr",
    //     },
    //     {
    //       field: "description",
    //       headerName: "Description",
    //       description: "",
    //       flex: 1,
    //       hide: false,
    //       hideable: false,
    //       sortable: true,
    //       sortingOrder: "asc",
    //       resizable: false,
    //       type: "string",
    //       align: "left",
    //       headerAlign: "left",
    //       hideSortIcons: false,
    //       disableColumnMenu: true,
    //       disableReorder: true,
    //       disableExport: true,
    //       maskable: false,
    //       isFixed: true,
    //       isEditable: true,
    //       sequence: 1,
    //       frozenColumn: false,
    //       footer: null,
    //       minWidth: 120,
    //       maxWidth: null,
    //       fieldName: null,
    //       edr: "edr",
    //     },
    //     {
    //       field: "uploadedOn",
    //       headerName: "Uploaded On",
    //       description: "",
    //       flex: 1,
    //       hide: false,
    //       hideable: false,
    //       sortable: true,
    //       sortingOrder: "asc",
    //       resizable: false,
    //       type: "string",
    //       align: "left",
    //       headerAlign: "left",
    //       hideSortIcons: false,
    //       disableColumnMenu: true,
    //       disableReorder: true,
    //       disableExport: true,
    //       maskable: false,
    //       isFixed: true,
    //       isEditable: true,
    //       sequence: 2,
    //       frozenColumn: false,
    //       footer: null,
    //       minWidth: null,
    //       maxWidth: null,
    //       fieldName: null,
    //       edr: "edr",
    //     },
    //   ],
    // };
    // return new Promise((resolve) =>
    //   setTimeout(() => resolve(RQFixedResponse), 1000),
    // );
  },

  createCollectionUploadedFiles: async (
    requestPayload: any,
  ): Promise<any | IServerError | IResponse> => {
    const response: AxiosResponse<any> = await http.post<any>(
      `/v1/direct-deposit-file/`,
      { filesData: requestPayload },
    );

    if (response.status === 201) {
      return {
        status: true,
        message: "Success",
        error: false,
      };
    } else if (response.status === 500) {
      return {
        status: false,
        message: "Something went wrong.",
        error: true,
      };
    }

    return {
      status: false,
      message: "Something went wrong please try again later.",
    };
  },

  // #################################################### collection Uploaded Parser ######################################################
  getCollectionUploadedTransacitonsParserList: async (
    id: string | undefined,
    data: IGetListRequest,
    navigateToLogout: (path: string) => void,
  ): Promise<IServerError | IResponse | null | any> => {
    if (!id) return null;

    try {
      const response: AxiosResponse<any> = await http.get<any>(
        `/v1/direct-deposit-file/${id}/deposit/parser/data`,
      );

      if (response.status === 200) {
        return response.data;
      } else if (response.status === 400) {
        return {
          status: false,
          message: "Bad Request.",
          error: true,
        };
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }
      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      if (axiosError && axiosError.status === 401) {
        navigateToLogout("/logout");
        return {
          status: false,
          message: "UnAuthrized",
        };
      } else {
        return {
          status: false,
          message: errorMessage,
        };
      }
    }
    // const RQFixedResponse: any = {
    //   header: [
    //     {
    //       id: 1,
    //       recordCount: "0000001",
    //       recordType: "A",
    //       code: "HDR",
    //       clientNumber: "654164116000",
    //       customerNumber: "WEBSENTIAL INC.",
    //       fileNumber: "0130",
    //       creationDate: "Mar 06 2024",
    //       currency: "CAN",
    //       inputType: "1",
    //       res: null,
    //       res2: null,
    //       res3: null,
    //       res4: null,
    //       filer: null,
    //       filer2: null,
    //       optionalRec: "N",
    //     },
    //   ],
    //   trailer: [
    //     {
    //       id: 1,
    //       recordCount: "0000001",
    //       recordType: "‍‍Z",
    //       code: "TRL",
    //       clientNumber: "7006420000",
    //       res: null,
    //       res2: null,
    //       totalTranssactions: "0000002",
    //       totalAmount: "00000001920790",
    //       totalClientOptionalRec: "00",
    //       totalCustomerOptionalRec: "000000",
    //       filer: null,
    //       res3: null,
    //       filer2: null,
    //       res4: null,
    //       filer3: null,
    //     },
    //   ],
    //   paymentRecord: [
    //     {
    //       id: 1,
    //       recordCount: "0000002",
    //       recordType: "D",
    //       code: "450",
    //       clientNumber: "123456",
    //       filer: null,
    //       customerNumber: "2408",
    //       paymentNumber: "00",
    //       financialInstitutionNumber: "0103",
    //       financialInstitutionBranchNumber: "03062",
    //       accountNo: "1111",
    //       filer2: null,
    //       amount: "0001915592",
    //       res: null,
    //       paymentDate: "2024066",
    //       customerName: "Zeeshan",
    //       lan: "E",
    //       paymentMedium: "E",
    //       clientShortName: "Malik",
    //       currency: "CAN",
    //       res2: null,
    //       desCountry: "CAN",
    //       filer3: null,
    //       res3: null,
    //       optionalRecord: "N",
    //       orders: [
    //         {
    //           id: 1,
    //           recordCount: "",
    //           recordType: "",
    //           code: "",
    //           clientNumber: "",
    //           filer: null,
    //           customerNumber: "2232, 2233, 2234, 1111, 4561",
    //           paymentNumber: "",
    //           financialInstitutionNumber: "03",
    //           financialInstitutionBranchNumber: "3062",
    //           accountNo: "5552585",
    //           filer2: null,
    //           amount: [
    //             {
    //               id: 1,
    //               type: "Payroll",
    //               clientName: "Zeeshan",
    //               amount: 8361.16,
    //               total: 8733.26,
    //             },
    //             {
    //               id: 2,
    //               type: "CRA",
    //               clientName: "Zeeshan",
    //               amount: 0,
    //               total: 8733.26,
    //             },
    //             {
    //               id: 3,
    //               type: "QC Remittance",
    //               clientName: "Zeeshan",
    //               amount: 372.1,
    //               total: 8733.26,
    //             },
    //           ],
    //           res: null,
    //           paymentDate: "Mar 06 2024",
    //           customerName: "zeeshan",
    //           lan: "",
    //           paymentMedium: "",
    //           clientShortName: "",
    //           currency: "",
    //           res2: null,
    //           desCountry: "",
    //           filer3: null,
    //           res3: null,
    //           optionalRecord: "",
    //         },
    //       ],
    //     },
    //     {
    //       id: 2,
    //       recordCount: "0000003",
    //       recordType: "D",
    //       code: "450",
    //       clientNumber: "123456",
    //       filer: null,
    //       customerNumber: "2409",
    //       paymentNumber: "01",
    //       financialInstitutionNumber: "5465",
    //       financialInstitutionBranchNumber: "00001",
    //       accountNo: "5646576544675465465",
    //       filer2: null,
    //       amount: "0000005198",
    //       res: null,
    //       paymentDate: "2024066",
    //       customerName: "WEBSENTIAL INC.",
    //       lan: "E",
    //       paymentMedium: "E",
    //       clientShortName: "Malik",
    //       currency: "CAN",
    //       res2: null,
    //       desCountry: "CAN",
    //       filer3: null,
    //       res3: null,
    //       optionalRecord: "N",
    //       orders: [
    //         {
    //           id: 2,
    //           recordCount: "",
    //           recordType: "",
    //           code: "",
    //           clientNumber: "",
    //           filer: null,
    //           customerNumber: "2236",
    //           paymentNumber: "",
    //           financialInstitutionNumber: "5465",
    //           financialInstitutionBranchNumber: "0021",
    //           accountNo: "5646576544675465465",
    //           filer2: null,
    //           amount: [
    //             {
    //               id: 1,
    //               type: "Invoice",
    //               clientName: "WEBSENTIAL INC.",
    //               amount: 51.1,
    //               total: 51.98,
    //             },
    //           ],
    //           res: null,
    //           paymentDate: "Mar 06 2024",
    //           customerName: "Websential Inc",
    //           lan: "",
    //           paymentMedium: "",
    //           clientShortName: "",
    //           currency: "",
    //           res2: null,
    //           desCountry: "",
    //           filer3: null,
    //           res3: null,
    //           optionalRecord: "",
    //         },
    //       ],
    //     },
    //   ],
    // };
    // return new Promise((resolve) =>
    //   setTimeout(() => resolve(RQFixedResponse), 1000),
    // );
  },

  // #################################################### collection Uploaded View ######################################################
  getCollectionUploadedTransacitonsViewList: async (
    id: string | undefined,
    data: IGetListRequest,
    navigateToLogout: (path: string) => void,
  ): Promise<IServerError | IResponse | null | any> => {
    if (!id) return null;

    try {
      const response: AxiosResponse<any> = await http.get<any>(
        `/v1/direct-deposit-file/${id}/deposit/view`,
      );

      if (response.status === 200) {
        return response.data;
      } else if (response.status === 400) {
        return {
          status: false,
          message: "Bad Request.",
          error: true,
        };
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }
      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      if (axiosError && axiosError.status === 401) {
        navigateToLogout("/logout");
        return {
          status: false,
          message: "UnAuthrized",
        };
      } else {
        return {
          status: false,
          message: errorMessage,
        };
      }
    }
    // const RQFixedResponse: any = {
    //   header: [
    //     {
    //       id: 1,
    //       recordCount: "0000001",
    //       recordType: "A",
    //       code: "HDR",
    //       clientNumber: "654164116000",
    //       customerNumber: "WEBSENTIAL INC.",
    //       fileNumber: "0130",
    //       creationDate: "Mar 06 2024",
    //       currency: "CAN",
    //       inputType: "1",
    //       res: null,
    //       res2: null,
    //       res3: null,
    //       res4: null,
    //       filer: null,
    //       filer2: null,
    //       optionalRec: "N",
    //     },
    //   ],
    //   trailer: [
    //     {
    //       id: 1,
    //       recordCount: "0000001",
    //       recordType: "‍‍Z",
    //       code: "TRL",
    //       clientNumber: "7006420000",
    //       res: null,
    //       res2: null,
    //       totalTranssactions: "0000002",
    //       totalAmount: "00000001920790",
    //       totalClientOptionalRec: "00",
    //       totalCustomerOptionalRec: "000000",
    //       filer: null,
    //       res3: null,
    //       filer2: null,
    //       res4: null,
    //       filer3: null,
    //     },
    //   ],
    //   paymentRecord: [
    //     {
    //       id: 1,
    //       recordCount: "0000002",
    //       recordType: "D",
    //       code: "450",
    //       clientNumber: "123456",
    //       filer: null,
    //       customerNumber: "2408",
    //       paymentNumber: "00",
    //       financialInstitutionNumber: "0103",
    //       financialInstitutionBranchNumber: "03062",
    //       accountNo: "1111",
    //       filer2: null,
    //       amount: "0001915592",
    //       res: null,
    //       paymentDate: "2024066",
    //       customerName: "Zeeshan",
    //       lan: "E",
    //       paymentMedium: "E",
    //       clientShortName: "Malik",
    //       currency: "CAN",
    //       res2: null,
    //       desCountry: "CAN",
    //       filer3: null,
    //       res3: null,
    //       optionalRecord: "N",
    //     },
    //     {
    //       id: 2,
    //       recordCount: "0000003",
    //       recordType: "D",
    //       code: "450",
    //       clientNumber: "123456",
    //       filer: null,
    //       customerNumber: "2409",
    //       paymentNumber: "01",
    //       financialInstitutionNumber: "5465",
    //       financialInstitutionBranchNumber: "00001",
    //       accountNo: "5646576544675465465",
    //       filer2: null,
    //       amount: "0000005198",
    //       res: null,
    //       paymentDate: "2024066",
    //       customerName: "WEBSENTIAL INC.",
    //       lan: "E",
    //       paymentMedium: "E",
    //       clientShortName: "Malik",
    //       currency: "CAN",
    //       res2: null,
    //       desCountry: "CAN",
    //       filer3: null,
    //       res3: null,
    //       optionalRecord: "N",
    //     },
    //   ],
    // };
    // return new Promise((resolve) =>
    //   setTimeout(() => resolve(RQFixedResponse), 1000),
    // );
  },

  // #################################################### payment Uploaded ######################################################
  getPaymentUploadedTransacitonsList: async (
    data: IGetListRequest,
    navigateToLogout: (path: string) => void,
  ): Promise<IServerError | IResponse | null | any> => {
    try {
      const response: AxiosResponse<any> = await http.get<any>(
        "/v1/direct-deposit-file/credit/file-list/data/api/?search_query=" +
          data.search_query +
          "&order_by=" +
          data.order_by +
          "&order_direction=" +
          data.order_direction +
          "&page=" +
          data.page +
          "&size=" +
          data.size,
      );

      if (response.status === 200) {
        return response.data;
      } else if (response.status === 400) {
        return {
          status: false,
          message: "Bad Request.",
          error: true,
        };
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }
      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      if (axiosError && axiosError.status === 401) {
        navigateToLogout("/logout");
        return {
          status: false,
          message: "UnAuthrized",
        };
      } else {
        return {
          status: false,
          message: errorMessage,
        };
      }
    }
    // const RQFixedResponse: any = {
    //   page: 1,
    //   size: 50,
    //   total: 12,
    //   items: [
    //     {
    //       id: 1,
    //       fileName: "PG-DEBIT-2022-07-19-1.txt",
    //       description: "210185.02",
    //       uploadedOn: "july 20 2022 08:16:15 AM",
    //     },
    //     {
    //       id: 2,
    //       fileName: "PG-DEBIT-2022-07-19-1.txt",
    //       description: "210185.02",
    //       uploadedOn: "july 20 2022 08:16:15 AM",
    //     },
    //     {
    //       id: 3,
    //       fileName: "PG-DEBIT-2022-07-19-1.txt",
    //       description: "210185.02",
    //       uploadedOn: "july 20 2022 08:16:15 AM",
    //     },
    //     {
    //       id: 4,
    //       fileName: "PG-DEBIT-2022-07-19-1.txt",
    //       description: "210185.02",
    //       uploadedOn: "july 20 2022 08:16:15 AM",
    //     },
    //   ],
    //   keyField: [
    //     {
    //       field: "id",
    //       templateUrl: "/rq-remittance-report/details/id",
    //     },
    //   ],
    //   headerColumns: [
    //     {
    //       field: "fileName",
    //       headerName: "File Name",
    //       description: "",
    //       flex: 1,
    //       hide: false,
    //       hideable: false,
    //       sortable: true,
    //       sortingOrder: "asc",
    //       resizable: false,
    //       type: "string",
    //       align: "left",
    //       headerAlign: "left",
    //       hideSortIcons: false,
    //       disableColumnMenu: true,
    //       disableReorder: true,
    //       disableExport: true,
    //       maskable: false,
    //       isFixed: true,
    //       isEditable: true,
    //       sequence: 0,
    //       frozenColumn: false,
    //       footer: null,
    //       minWidth: 50,
    //       maxWidth: null,
    //       fieldName: null,
    //       edr: "edr",
    //     },
    //     {
    //       field: "description",
    //       headerName: "Description",
    //       description: "",
    //       flex: 1,
    //       hide: false,
    //       hideable: false,
    //       sortable: true,
    //       sortingOrder: "asc",
    //       resizable: false,
    //       type: "string",
    //       align: "left",
    //       headerAlign: "left",
    //       hideSortIcons: false,
    //       disableColumnMenu: true,
    //       disableReorder: true,
    //       disableExport: true,
    //       maskable: false,
    //       isFixed: true,
    //       isEditable: true,
    //       sequence: 1,
    //       frozenColumn: false,
    //       footer: null,
    //       minWidth: 120,
    //       maxWidth: null,
    //       fieldName: null,
    //       edr: "edr",
    //     },
    //     {
    //       field: "uploadedOn",
    //       headerName: "Uploaded On",
    //       description: "",
    //       flex: 1,
    //       hide: false,
    //       hideable: false,
    //       sortable: true,
    //       sortingOrder: "asc",
    //       resizable: false,
    //       type: "string",
    //       align: "left",
    //       headerAlign: "left",
    //       hideSortIcons: false,
    //       disableColumnMenu: true,
    //       disableReorder: true,
    //       disableExport: true,
    //       maskable: false,
    //       isFixed: true,
    //       isEditable: true,
    //       sequence: 2,
    //       frozenColumn: false,
    //       footer: null,
    //       minWidth: null,
    //       maxWidth: null,
    //       fieldName: null,
    //       edr: "edr",
    //     },
    //   ],
    // };
    // return new Promise((resolve) =>
    //   setTimeout(() => resolve(RQFixedResponse), 1000),
    // );
  },

  createPaymentUploadedFiles: async (
    requestPayload: any,
  ): Promise<any | IServerError | IResponse> => {
    const response: AxiosResponse<any> = await http.post<any>(
      `/v1/direct-deposit-file/credit/`,
      { filesData: requestPayload },
    );

    if (response.status === 201) {
      return {
        status: true,
        message: "Success",
        error: false,
      };
    } else if (response.status === 500) {
      return {
        status: false,
        message: "Something went wrong.",
        error: true,
      };
    }

    return {
      status: false,
      message: "Something went wrong please try again later.",
    };
  },

  // #################################################### payment Uploaded Parser ######################################################
  getPaymentUploadedTransacitonsParserList: async (
    id: string | undefined,
    data: IGetListRequest,
    navigateToLogout: (path: string) => void,
  ): Promise<IServerError | IResponse | null | any> => {
    if (!id) return null;

    try {
      const response: AxiosResponse<any> = await http.get<any>(
        `/v1/direct-deposit-file/${id}/credit/file/parser/data/`,
      );

      if (response.status === 200) {
        return response.data;
      } else if (response.status === 400) {
        return {
          status: false,
          message: "Bad Request.",
          error: true,
        };
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }
      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      if (axiosError && axiosError.status === 401) {
        navigateToLogout("/logout");
        return {
          status: false,
          message: "UnAuthrized",
        };
      } else {
        return {
          status: false,
          message: errorMessage,
        };
      }
    }
    // const RQFixedResponse: any = {
    //   header: [
    //     {
    //       id: 1,
    //       recordCount: "0000001",
    //       recordType: "A",
    //       code: "HDR",
    //       clientNumber: "654164116000",
    //       customerNumber: "WEBSENTIAL INC.",
    //       fileNumber: "0130",
    //       creationDate: "Mar 06 2024",
    //       currency: "CAN",
    //       inputType: "1",
    //       res: null,
    //       res2: null,
    //       res3: null,
    //       res4: null,
    //       filer: null,
    //       filer2: null,
    //       optionalRec: "N",
    //     },
    //   ],
    //   trailer: [
    //     {
    //       id: 1,
    //       recordCount: "0000001",
    //       recordType: "‍‍Z",
    //       code: "TRL",
    //       clientNumber: "7006420000",
    //       res: null,
    //       res2: null,
    //       totalTranssactions: "0000002",
    //       totalAmount: "00000001920790",
    //       totalClientOptionalRec: "00",
    //       totalCustomerOptionalRec: "000000",
    //       filer: null,
    //       res3: null,
    //       filer2: null,
    //       res4: null,
    //       filer3: null,
    //     },
    //   ],
    //   paymentRecord: [
    //     {
    //       id: 1,
    //       recordCount: "0000002",
    //       recordType: "D",
    //       code: "450",
    //       clientNumber: "123456",
    //       filer: null,
    //       customerNumber: "2408",
    //       paymentNumber: "00",
    //       financialInstitutionNumber: "0103",
    //       financialInstitutionBranchNumber: "03062",
    //       accountNo: "1111",
    //       filer2: null,
    //       amount: "0001915592",
    //       res: null,
    //       paymentDate: "2024066",
    //       customerName: "Zeeshan",
    //       lan: "E",
    //       paymentMedium: "E",
    //       clientShortName: "Malik",
    //       currency: "CAN",
    //       res2: null,
    //       desCountry: "CAN",
    //       filer3: null,
    //       res3: null,
    //       optionalRecord: "N",
    //       orders: [
    //         {
    //           id: 1,
    //           recordCount: "",
    //           recordType: "",
    //           code: "",
    //           clientNumber: "",
    //           filer: null,
    //           customerNumber: "2232, 2233, 2234, 1111, 4561",
    //           paymentNumber: "",
    //           financialInstitutionNumber: "03",
    //           financialInstitutionBranchNumber: "3062",
    //           accountNo: "5552585",
    //           filer2: null,
    //           amount: [
    //             {
    //               id: 1,
    //               type: "Payroll",
    //               clientName: "Zeeshan",
    //               amount: 8361.16,
    //               total: 8733.26,
    //             },
    //             {
    //               id: 2,
    //               type: "CRA",
    //               clientName: "Zeeshan",
    //               amount: 0,
    //               total: 8733.26,
    //             },
    //             {
    //               id: 3,
    //               type: "QC Remittance",
    //               clientName: "Zeeshan",
    //               amount: 372.1,
    //               total: 8733.26,
    //             },
    //           ],
    //           res: null,
    //           paymentDate: "Mar 06 2024",
    //           customerName: "zeeshan",
    //           lan: "",
    //           paymentMedium: "",
    //           clientShortName: "",
    //           currency: "",
    //           res2: null,
    //           desCountry: "",
    //           filer3: null,
    //           res3: null,
    //           optionalRecord: "",
    //         },
    //       ],
    //     },
    //     {
    //       id: 2,
    //       recordCount: "0000003",
    //       recordType: "D",
    //       code: "450",
    //       clientNumber: "123456",
    //       filer: null,
    //       customerNumber: "2409",
    //       paymentNumber: "01",
    //       financialInstitutionNumber: "5465",
    //       financialInstitutionBranchNumber: "00001",
    //       accountNo: "5646576544675465465",
    //       filer2: null,
    //       amount: "0000005198",
    //       res: null,
    //       paymentDate: "2024066",
    //       customerName: "WEBSENTIAL INC.",
    //       lan: "E",
    //       paymentMedium: "E",
    //       clientShortName: "Malik",
    //       currency: "CAN",
    //       res2: null,
    //       desCountry: "CAN",
    //       filer3: null,
    //       res3: null,
    //       optionalRecord: "N",
    //       orders: [
    //         {
    //           id: 2,
    //           recordCount: "",
    //           recordType: "",
    //           code: "",
    //           clientNumber: "",
    //           filer: null,
    //           customerNumber: "2236",
    //           paymentNumber: "",
    //           financialInstitutionNumber: "5465",
    //           financialInstitutionBranchNumber: "0021",
    //           accountNo: "5646576544675465465",
    //           filer2: null,
    //           amount: [
    //             {
    //               id: 1,
    //               type: "Invoice",
    //               clientName: "WEBSENTIAL INC.",
    //               amount: 51.1,
    //               total: 51.98,
    //             },
    //           ],
    //           res: null,
    //           paymentDate: "Mar 06 2024",
    //           customerName: "Websential Inc",
    //           lan: "",
    //           paymentMedium: "",
    //           clientShortName: "",
    //           currency: "",
    //           res2: null,
    //           desCountry: "",
    //           filer3: null,
    //           res3: null,
    //           optionalRecord: "",
    //         },
    //       ],
    //     },
    //   ],
    // };
    // return new Promise((resolve) =>
    //   setTimeout(() => resolve(RQFixedResponse), 1000),
    // );
  },

  // #################################################### payment Uploaded View ######################################################
  getPaymentUploadedTransacitonsViewList: async (
    id: string | undefined,
    data: IGetListRequest,
    navigateToLogout: (path: string) => void,
  ): Promise<IServerError | IResponse | null | any> => {
    if (!id) return null;

    try {
      const response: AxiosResponse<any> = await http.get<any>(
        `/v1/direct-deposit-file/${id}/credit/file/view`,
      );

      if (response.status === 200) {
        return response.data;
      } else if (response.status === 400) {
        return {
          status: false,
          message: "Bad Request.",
          error: true,
        };
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }
      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      if (axiosError && axiosError.status === 401) {
        navigateToLogout("/logout");
        return {
          status: false,
          message: "UnAuthrized",
        };
      } else {
        return {
          status: false,
          message: errorMessage,
        };
      }
    }
    // const RQFixedResponse: any = {
    //   header: [
    //     {
    //       id: 1,
    //       recordCount: "0000001",
    //       recordType: "A",
    //       code: "HDR",
    //       clientNumber: "654164116000",
    //       customerNumber: "WEBSENTIAL INC.",
    //       fileNumber: "0130",
    //       creationDate: "Mar 06 2024",
    //       currency: "CAN",
    //       inputType: "1",
    //       res: null,
    //       res2: null,
    //       res3: null,
    //       res4: null,
    //       filer: null,
    //       filer2: null,
    //       optionalRec: "N",
    //     },
    //   ],
    //   trailer: [
    //     {
    //       id: 1,
    //       recordCount: "0000001",
    //       recordType: "‍‍Z",
    //       code: "TRL",
    //       clientNumber: "7006420000",
    //       res: null,
    //       res2: null,
    //       totalTranssactions: "0000002",
    //       totalAmount: "00000001920790",
    //       totalClientOptionalRec: "00",
    //       totalCustomerOptionalRec: "000000",
    //       filer: null,
    //       res3: null,
    //       filer2: null,
    //       res4: null,
    //       filer3: null,
    //     },
    //   ],
    //   paymentRecord: [
    //     {
    //       id: 1,
    //       recordCount: "0000002",
    //       recordType: "D",
    //       code: "450",
    //       clientNumber: "123456",
    //       filer: null,
    //       customerNumber: "2408",
    //       paymentNumber: "00",
    //       financialInstitutionNumber: "0103",
    //       financialInstitutionBranchNumber: "03062",
    //       accountNo: "1111",
    //       filer2: null,
    //       amount: "0001915592",
    //       res: null,
    //       paymentDate: "2024066",
    //       customerName: "Zeeshan",
    //       lan: "E",
    //       paymentMedium: "E",
    //       clientShortName: "Malik",
    //       currency: "CAN",
    //       res2: null,
    //       desCountry: "CAN",
    //       filer3: null,
    //       res3: null,
    //       optionalRecord: "N",
    //     },
    //     {
    //       id: 2,
    //       recordCount: "0000003",
    //       recordType: "D",
    //       code: "450",
    //       clientNumber: "123456",
    //       filer: null,
    //       customerNumber: "2409",
    //       paymentNumber: "01",
    //       financialInstitutionNumber: "5465",
    //       financialInstitutionBranchNumber: "00001",
    //       accountNo: "5646576544675465465",
    //       filer2: null,
    //       amount: "0000005198",
    //       res: null,
    //       paymentDate: "2024066",
    //       customerName: "WEBSENTIAL INC.",
    //       lan: "E",
    //       paymentMedium: "E",
    //       clientShortName: "Malik",
    //       currency: "CAN",
    //       res2: null,
    //       desCountry: "CAN",
    //       filer3: null,
    //       res3: null,
    //       optionalRecord: "N",
    //     },
    //   ],
    // };
    // return new Promise((resolve) =>
    //   setTimeout(() => resolve(RQFixedResponse), 1000),
    // );
  },

  // #################################################### collection voucher list ######################################################
  getCollectionVoucherListData: async (
    data: IGetListRequest,
  ): Promise<IServerError | IResponse | null | any> => {
    try {
      const response: AxiosResponse<any> = await http.get<any>(
        "/v1/collection-voucher-setup/?" +
          "&order_by=" +
          data.order_by +
          "&order_direction=" +
          data.order_direction +
          "&page=" +
          data.page +
          "&size=" +
          data.size,
      );

      if (response.status === 200) {
        return response.data;
      } else if (response.status === 400) {
        return {
          status: false,
          message: "Bad Request.",
          error: true,
        };
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }
      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      if (axiosError && axiosError.status === 401) {
        // navigateToLogout("/logout");
        return {
          status: false,
          message: "UnAuthrized",
        };
      } else {
        return {
          status: false,
          message: errorMessage,
        };
      }
    }
    // const RQFixedResponse: any = {
    //   page: 1,
    //   size: 50,
    //   total: 12,
    //   items: [
    //     {
    //       id: 1,
    //       account: "PG-DEBIT-2022-07-19-1.txt",
    //       client: "210185.02",
    //       employee: "july 20 2022 08:16:15 AM",
    //       amount:"12345.00",
    //       date:"july 20 2022 08:16:15 AM",
    //       comment:"hello"
    //     },
    //     {
    //       id: 2,
    //      account: "PG-DEBIT-2022-07-19-1.txt",
    //       client: "210185.02",
    //       employee: "july 20 2022 08:16:15 AM",
    //       amount:"12345.00",
    //       date:"july 20 2022 08:16:15 AM",
    //       comment:"hello"
    //     },
    //   ],
    //   keyField: [
    //     {
    //       field: "id",
    //       templateUrl: "/rq-remittance-report/details/id",
    //     },
    //   ],
    //   headerColumns: [
    //     {
    //       field: "account",
    //       headerName: "Account",
    //       description: "",
    //       flex: 1,
    //       hide: false,
    //       hideable: false,
    //       sortable: true,
    //       sortingOrder: "asc",
    //       resizable: false,
    //       type: "string",
    //       align: "left",
    //       headerAlign: "left",
    //       hideSortIcons: false,
    //       disableColumnMenu: true,
    //       disableReorder: true,
    //       disableExport: true,
    //       maskable: false,
    //       isFixed: true,
    //       isEditable: true,
    //       sequence: 0,
    //       frozenColumn: false,
    //       footer: null,
    //       minWidth: 50,
    //       maxWidth: null,
    //       fieldName: null,
    //       edr: "edr",
    //     },
    //     {
    //       field: "client",
    //       headerName: "Client",
    //       description: "",
    //       flex: 1,
    //       hide: false,
    //       hideable: false,
    //       sortable: true,
    //       sortingOrder: "asc",
    //       resizable: false,
    //       type: "string",
    //       align: "left",
    //       headerAlign: "left",
    //       hideSortIcons: false,
    //       disableColumnMenu: true,
    //       disableReorder: true,
    //       disableExport: true,
    //       maskable: false,
    //       isFixed: true,
    //       isEditable: true,
    //       sequence: 1,
    //       frozenColumn: false,
    //       footer: null,
    //       minWidth: 120,
    //       maxWidth: null,
    //       fieldName: null,
    //       edr: "edr",
    //     },
    //     {
    //       field: "employee",
    //       headerName: "Employee",
    //       description: "",
    //       flex: 1,
    //       hide: false,
    //       hideable: false,
    //       sortable: true,
    //       sortingOrder: "asc",
    //       resizable: false,
    //       type: "string",
    //       align: "left",
    //       headerAlign: "left",
    //       hideSortIcons: false,
    //       disableColumnMenu: true,
    //       disableReorder: true,
    //       disableExport: true,
    //       maskable: false,
    //       isFixed: true,
    //       isEditable: true,
    //       sequence: 2,
    //       frozenColumn: false,
    //       footer: null,
    //       minWidth: null,
    //       maxWidth: null,
    //       fieldName: null,
    //       edr: "edr",
    //     },
    //      {
    //       field: "amount",
    //       headerName: "Amount",
    //       description: "",
    //       flex: 1,
    //       hide: false,
    //       hideable: false,
    //       sortable: true,
    //       sortingOrder: "asc",
    //       resizable: false,
    //       type: "string",
    //       align: "left",
    //       headerAlign: "left",
    //       hideSortIcons: false,
    //       disableColumnMenu: true,
    //       disableReorder: true,
    //       disableExport: true,
    //       maskable: false,
    //       isFixed: true,
    //       isEditable: true,
    //       sequence: 2,
    //       frozenColumn: false,
    //       footer: null,
    //       minWidth: null,
    //       maxWidth: null,
    //       fieldName: null,
    //       edr: "edr",
    //     },
    //      {
    //       field: "date",
    //       headerName: "Date",
    //       description: "",
    //       flex: 1,
    //       hide: false,
    //       hideable: false,
    //       sortable: true,
    //       sortingOrder: "asc",
    //       resizable: false,
    //       type: "string",
    //       align: "left",
    //       headerAlign: "left",
    //       hideSortIcons: false,
    //       disableColumnMenu: true,
    //       disableReorder: true,
    //       disableExport: true,
    //       maskable: false,
    //       isFixed: true,
    //       isEditable: true,
    //       sequence: 2,
    //       frozenColumn: false,
    //       footer: null,
    //       minWidth: null,
    //       maxWidth: null,
    //       fieldName: null,
    //       edr: "edr",
    //     },
    //      {
    //       field: "comment",
    //       headerName: "Comment",
    //       description: "",
    //       flex: 1,
    //       hide: false,
    //       hideable: false,
    //       sortable: true,
    //       sortingOrder: "asc",
    //       resizable: false,
    //       type: "string",
    //       align: "left",
    //       headerAlign: "left",
    //       hideSortIcons: false,
    //       disableColumnMenu: true,
    //       disableReorder: true,
    //       disableExport: true,
    //       maskable: false,
    //       isFixed: true,
    //       isEditable: true,
    //       sequence: 2,
    //       frozenColumn: false,
    //       footer: null,
    //       minWidth: null,
    //       maxWidth: null,
    //       fieldName: null,
    //       edr: "edr",
    //     },
    //   ],
    // };
    // return new Promise((resolve) =>
    //   setTimeout(() => resolve(RQFixedResponse), 1000),
    // );
  },

  // #################################################### collection voucher setup ######################################################
  getCollectionVoucherData: async (
    id: string | undefined,
    navigateToLogout: (path: string) => void,
  ): Promise<IServerError | IResponse | null | any> => {
    if (!id) return null;
    try {
      const response: AxiosResponse<any> = await http.get<any>(
        `/v1/collection-voucher-setup/${id}/`,
      );
      if (response.status === 200) {
        return response.data;
      } else if (response.status === 400) {
        return {
          status: false,
          message: "Bad Request.",
          error: true,
        };
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }
      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      if (axiosError && axiosError.status === 401) {
        navigateToLogout("/logout");
        return {
          status: false,
          message: "UnAuthrized",
        };
      } else {
        return {
          status: false,
          message: errorMessage,
        };
      }
    }
  },
  saveAsDraftCollectionVoucher: async (
    requestPayload: any,
  ): Promise<IServerError | IResponse | any> => {
    try {
      const response: AxiosResponse<any> = await http.post<any>(
        `/v1/collection-voucher-setup/add-collection-voucher`,
        requestPayload,
      );

      if (response.status === 201) {
        return response;
      } else if (response.status === 400) {
        return response.data;
      } else if (response.status === 401) {
        // window.location.href = '/logout';
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      if (axiosError && axiosError.status === 401) {
        requestPayload.navigateToLogout("/logout");
        return {
          status: false,
          message: "UnAuthrized",
        };
      } else if (axiosError && axiosError.status === 422) {
        return {
          status: false,
          message: `Errors : \n${format422Errors(axiosError?.response?.data)}`,
        };
      } else {
        return {
          status: false,
          message: errorMessage,
        };
      }
    }
  },
  createSaveandPostCollectionVoucherData: async (
    requestPayload: any,
  ): Promise<any | IServerError | IResponse | any> => {
    try {
      const response: AxiosResponse<any> = await http.post<any>(
        `/v1/save-and-post`,
        // requestPayload
      );

      if (response.status === 201) {
        return response;
      } else if (response.status === 400) {
        return response.data;
      } else if (response.status === 401) {
        // window.location.href = '/logout';
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      return {
        status: false,
        message: errorMessage,
      };
    }
  },
  deleteCollectionVoucherById: async (
    requestPayload: any,
  ): Promise<any | IServerError | IResponse | any> => {
    try {
      const response: AxiosResponse<any> = await http.delete<any>(
        `/v1/collection-voucher-setup/${requestPayload.collectionVoucherId}/`,
        // requestPayload
      );

      if (response.status === 200) {
        return response;
      } else if (response.status === 400) {
        return response.data;
      } else if (response.status === 401) {
        // window.location.href = '/logout';
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      if (axiosError && axiosError.status === 401) {
        requestPayload.navigateToLogout("/logout");
        return {
          status: false,
          message: "UnAuthrized",
        };
      } else if (axiosError && axiosError.status === 422) {
        return {
          status: false,
          message: `Errors : \n${format422Errors(axiosError?.response?.data)}`,
        };
      } else {
        return {
          status: false,
          message: errorMessage,
        };
      }
    }
  },
  // #################################################### payment voucher list ######################################################
  getPaymentVoucherListData: async (
    data: IGetListRequest,
  ): Promise<IServerError | IResponse | null | any> => {
    try {
      const response: AxiosResponse<any> = await http.get<any>(
        "/v1/payment-voucher-setup/?" +
          "&order_by=" +
          data.order_by +
          "&order_direction=" +
          data.order_direction +
          "&page=" +
          data.page +
          "&size=" +
          data.size,
      );

      if (response.status === 200) {
        return response.data;
      } else if (response.status === 400) {
        return {
          status: false,
          message: "Bad Request.",
          error: true,
        };
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }
      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      if (axiosError && axiosError.status === 401) {
        // navigateToLogout("/logout");
        return {
          status: false,
          message: "UnAuthrized",
        };
      } else {
        return {
          status: false,
          message: errorMessage,
        };
      }
    }
    // const RQFixedResponse: any = {
    //   page: 1,
    //   size: 50,
    //   total: 12,
    //   items: [
    //     {
    //       id: 1,
    //       account: "PG-DEBIT-2022-07-19-1.txt",
    //       client: "210185.02",
    //       employee: "july 20 2022 08:16:15 AM",
    //       amount:"12345.00",
    //       date:"july 20 2022 08:16:15 AM",
    //       comment:"hello"
    //     },
    //     {
    //       id: 2,
    //      account: "PG-DEBIT-2022-07-19-1.txt",
    //       client: "210185.02",
    //       employee: "july 20 2022 08:16:15 AM",
    //       amount:"12345.00",
    //       date:"july 20 2022 08:16:15 AM",
    //       comment:"hello"
    //     },
    //   ],
    //   keyField: [
    //     {
    //       field: "id",
    //       templateUrl: "/rq-remittance-report/details/id",
    //     },
    //   ],
    //   headerColumns: [
    //     {
    //       field: "account",
    //       headerName: "Account",
    //       description: "",
    //       flex: 1,
    //       hide: false,
    //       hideable: false,
    //       sortable: true,
    //       sortingOrder: "asc",
    //       resizable: false,
    //       type: "string",
    //       align: "left",
    //       headerAlign: "left",
    //       hideSortIcons: false,
    //       disableColumnMenu: true,
    //       disableReorder: true,
    //       disableExport: true,
    //       maskable: false,
    //       isFixed: true,
    //       isEditable: true,
    //       sequence: 0,
    //       frozenColumn: false,
    //       footer: null,
    //       minWidth: 50,
    //       maxWidth: null,
    //       fieldName: null,
    //       edr: "edr",
    //     },
    //     {
    //       field: "client",
    //       headerName: "Client",
    //       description: "",
    //       flex: 1,
    //       hide: false,
    //       hideable: false,
    //       sortable: true,
    //       sortingOrder: "asc",
    //       resizable: false,
    //       type: "string",
    //       align: "left",
    //       headerAlign: "left",
    //       hideSortIcons: false,
    //       disableColumnMenu: true,
    //       disableReorder: true,
    //       disableExport: true,
    //       maskable: false,
    //       isFixed: true,
    //       isEditable: true,
    //       sequence: 1,
    //       frozenColumn: false,
    //       footer: null,
    //       minWidth: 120,
    //       maxWidth: null,
    //       fieldName: null,
    //       edr: "edr",
    //     },
    //     {
    //       field: "employee",
    //       headerName: "Employee",
    //       description: "",
    //       flex: 1,
    //       hide: false,
    //       hideable: false,
    //       sortable: true,
    //       sortingOrder: "asc",
    //       resizable: false,
    //       type: "string",
    //       align: "left",
    //       headerAlign: "left",
    //       hideSortIcons: false,
    //       disableColumnMenu: true,
    //       disableReorder: true,
    //       disableExport: true,
    //       maskable: false,
    //       isFixed: true,
    //       isEditable: true,
    //       sequence: 2,
    //       frozenColumn: false,
    //       footer: null,
    //       minWidth: null,
    //       maxWidth: null,
    //       fieldName: null,
    //       edr: "edr",
    //     },
    //      {
    //       field: "amount",
    //       headerName: "Amount",
    //       description: "",
    //       flex: 1,
    //       hide: false,
    //       hideable: false,
    //       sortable: true,
    //       sortingOrder: "asc",
    //       resizable: false,
    //       type: "string",
    //       align: "left",
    //       headerAlign: "left",
    //       hideSortIcons: false,
    //       disableColumnMenu: true,
    //       disableReorder: true,
    //       disableExport: true,
    //       maskable: false,
    //       isFixed: true,
    //       isEditable: true,
    //       sequence: 2,
    //       frozenColumn: false,
    //       footer: null,
    //       minWidth: null,
    //       maxWidth: null,
    //       fieldName: null,
    //       edr: "edr",
    //     },
    //      {
    //       field: "date",
    //       headerName: "Date",
    //       description: "",
    //       flex: 1,
    //       hide: false,
    //       hideable: false,
    //       sortable: true,
    //       sortingOrder: "asc",
    //       resizable: false,
    //       type: "string",
    //       align: "left",
    //       headerAlign: "left",
    //       hideSortIcons: false,
    //       disableColumnMenu: true,
    //       disableReorder: true,
    //       disableExport: true,
    //       maskable: false,
    //       isFixed: true,
    //       isEditable: true,
    //       sequence: 2,
    //       frozenColumn: false,
    //       footer: null,
    //       minWidth: null,
    //       maxWidth: null,
    //       fieldName: null,
    //       edr: "edr",
    //     },
    //      {
    //       field: "comment",
    //       headerName: "Comment",
    //       description: "",
    //       flex: 1,
    //       hide: false,
    //       hideable: false,
    //       sortable: true,
    //       sortingOrder: "asc",
    //       resizable: false,
    //       type: "string",
    //       align: "left",
    //       headerAlign: "left",
    //       hideSortIcons: false,
    //       disableColumnMenu: true,
    //       disableReorder: true,
    //       disableExport: true,
    //       maskable: false,
    //       isFixed: true,
    //       isEditable: true,
    //       sequence: 2,
    //       frozenColumn: false,
    //       footer: null,
    //       minWidth: null,
    //       maxWidth: null,
    //       fieldName: null,
    //       edr: "edr",
    //     },
    //   ],
    // };
    // return new Promise((resolve) =>
    //   setTimeout(() => resolve(RQFixedResponse), 1000),
    // );
  },

  // #################################################### payment voucher setup ######################################################
  getPaymentVoucherData: async (
    id: string | undefined,
    navigateToLogout: (path: string) => void,
  ): Promise<IServerError | IResponse | null | any> => {
    if (!id) return null;
    try {
      const response: AxiosResponse<any> = await http.get<any>(
        `/v1/payment-voucher-setup/${id}/`,
      );
      if (response.status === 200) {
        return response.data;
      } else if (response.status === 400) {
        return {
          status: false,
          message: "Bad Request.",
          error: true,
        };
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }
      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      if (axiosError && axiosError.status === 401) {
        navigateToLogout("/logout");
        return {
          status: false,
          message: "UnAuthrized",
        };
      } else {
        return {
          status: false,
          message: errorMessage,
        };
      }
    }
  },
  saveAsDraftPaymentVoucher: async (
    requestPayload: any,
  ): Promise<IServerError | IResponse | any> => {
    try {
      const response: AxiosResponse<any> = await http.post<any>(
        `/v1/payment-voucher-setup/add-payment-voucher`,
        requestPayload,
      );

      if (response.status === 201) {
        return response;
      } else if (response.status === 400) {
        return response.data;
      } else if (response.status === 401) {
        // window.location.href = '/logout';
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      if (axiosError && axiosError.status === 401) {
        requestPayload.navigateToLogout("/logout");
        return {
          status: false,
          message: "UnAuthrized",
        };
      } else if (axiosError && axiosError.status === 422) {
        return {
          status: false,
          message: `Errors : \n${format422Errors(axiosError?.response?.data)}`,
        };
      } else {
        return {
          status: false,
          message: errorMessage,
        };
      }
    }
  },
  createSaveandPostPaymentVoucherData: async (
    requestPayload: any,
  ): Promise<any | IServerError | IResponse | any> => {
    try {
      const response: AxiosResponse<any> = await http.post<any>(
        `/v1/save-and-post`,
        // requestPayload
      );

      if (response.status === 201) {
        return response;
      } else if (response.status === 400) {
        return response.data;
      } else if (response.status === 401) {
        // window.location.href = '/logout';
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      return {
        status: false,
        message: errorMessage,
      };
    }
  },
  deletePaymentVoucherById: async (
    requestPayload: any,
  ): Promise<any | IServerError | IResponse | any> => {
    try {
      const response: AxiosResponse<any> = await http.delete<any>(
        `/v1/delete/`,
        // requestPayload
      );

      if (response.status === 201) {
        return response;
      } else if (response.status === 400) {
        return response.data;
      } else if (response.status === 401) {
        // window.location.href = '/logout';
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      if (axiosError && axiosError.status === 401) {
        requestPayload.navigateToLogout("/logout");
        return {
          status: false,
          message: "UnAuthrized",
        };
      } else if (axiosError && axiosError.status === 422) {
        return {
          status: false,
          message: `Errors : \n${format422Errors(axiosError?.response?.data)}`,
        };
      } else {
        return {
          status: false,
          message: errorMessage,
        };
      }
    }
  },

  getPeriodTypesByClientId: async (
    clientId: string,
  ): Promise<any | IServerError | IResponse | null> => {
    const response: AxiosResponse<any[]> = await http.get<any[]>(
      `/v1/collection-voucher-setup/get_period_types_ddl_by_client_id?client_id=${clientId}`,
    );

    if (response.status === 200) {
      return response.data;
    }

    return {
      status: false,
      message: "Something went wrong please try again later.",
    };
  },
  getPeriodDatesByScheduleId: async (
    scheduleId: string,
  ): Promise<any | IServerError | IResponse | null> => {
    const response: AxiosResponse<any[]> = await http.get<any[]>(
      `/v1/collection-voucher-setup/get_period_dates_ddl_by_id?schedule_id=${scheduleId}`,
    );

    if (response.status === 200) {
      return response.data;
    }

    return {
      status: false,
      message: "Something went wrong please try again later.",
    };
  },
  getClientsAccumulatedAmounts: async (
    clientIds: any,
  ): Promise<any | IServerError | IResponse | null | any> => {
    try {
      const response: AxiosResponse<any> = await http.put<any>(
        `/v1/payroll-wire-transfer/get/accumulated/amount/`,
        clientIds,
      );

      if (response.status === 200) {
        return response.data;
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      return {
        status: false,
        message: errorMessage,
      };
    }

    // let data = [
    //   {
    //     clientId: "6a0b02877fd71cc0b1b4c547",
    //     clientName: "M Ahmad Software Engineer ",
    //     accumulatedAmount: -27796.38,
    //   },
    //   // {
    //   //   clientId: "6a0b02877fd71cc0b1b4c547",
    //   //   accumulatedAmount: 90.42,
    //   // },
    // ];
    // return data;
  },

  getDirectDepositRollBackTransactionsList: async (
    runGetListAPICall: boolean,
    accountant: string,
    client: string,
    clientType: string | number,
    // type: string,
    employee: string,
    transactionMethod: string | number | null,
    dateAppliesOn: string | number,
    startDate: string | null,
    endDate: string | null,
    data: IGetListRequest,
    navigateToLogout: (path: string) => void,
  ): Promise<IServerError | IResponse | null | any> => {
    if (!runGetListAPICall) return null;
    if (!accountant) return null;
    if (!employee) return null;

    try {
      // client=dss&employee=sdssd&order_by=name&order_direction=asc&page=1&size=50
      const response: AxiosResponse<any> = await http.get<any>(
        "/v1/direct-deposit-roll-back/get-roll-back-data-list?accountant_id=" +
          accountant +
          "&client_id=" +
          client +
          "&employee_id=" +
          employee +
          "&client_type=" +
          clientType +
          "&date_applies_on=" +
          dateAppliesOn +
          (startDate ? "&start_date=" + startDate : "") +
          (endDate ? "&end_date=" + endDate : "") +
          "&search_query=" +
          data.search_query +
          "&order_by=" +
          data.order_by +
          "&order_direction=" +
          data.order_direction +
          "&page=" +
          data.page +
          "&size=" +
          data.size,
      );

      if (response.status === 200) {
        return response.data;
      } else if (response.status === 400) {
        return {
          status: false,
          message: "Bad Request.",
          error: true,
        };
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }
      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      if (axiosError && axiosError.status === 401) {
        navigateToLogout("/logout");
        return {
          status: false,
          message: "UnAuthrized",
        };
      } else {
        return {
          status: false,
          message: errorMessage,
        };
      }
    }
  },
  confirmRollBackTransactions: async (
    requestPayload: any,
  ): Promise<any | IServerError | IResponse | any> => {
    try {
      const response: AxiosResponse<any> = await http.post<any>(
        `/v1/direct-deposit-roll-back/process-roll-back/transaction-data/?transaction_next_screen=${requestPayload.transactionNextScreenTag}`,
        // { rollBackList: requestPayload },
        // requestPayload,
        {
          rollBackList: requestPayload.data,
        },
      );

      if (response.status === 201) {
        return response;
      } else if (response.status === 400) {
        return response.data;
      } else if (response.status === 401) {
        // window.location.href = '/logout';
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      if (axiosError && axiosError.status === 401) {
        requestPayload.navigateToLogout("/logout");
        return {
          status: false,
          message: "UnAuthrized",
        };
      } else if (axiosError && axiosError.status === 422) {
        return {
          status: false,
          message: `Errors : \n${format422Errors(axiosError?.response?.data)}`,
        };
      } else {
        return {
          status: false,
          message: errorMessage,
        };
      }
    }
  },
};
